import { useState, useMemo, useCallback, useEffect, useRef, type CSSProperties } from 'react';
import { DeleteInvoiceModal } from './DeleteInvoiceModal';
import { deleteInvoiceCascade } from '../../../services/delete-invoice-service';
import { createPatientPackagesFromInvoice } from '../../../services/patient-package-service';
import { Button } from '@helios/ui/button';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qInvoicesByPatient,
  qInvoicesByEncounter,
  // PRD.229 follow-up — date-scoped invoices for the encounter Invoices tab.
  qInvoicesByPatientAndDate,
  qNoOp,
  qInvoiceItems,
  qActiveAppointmentServices,
  qInventoryPickerCandidates,
  qInvoiceItemDispenseStatuses,
  qInvoiceProductReleaseStatuses,
  qAvailableBatchesForProductRelease,
  // PRD.209 — encounter-aware "Add Service from Appointment" action.
  qAppointmentById,
  qAppointmentServiceById,
  qAppointmentsByPatient,
  qEncounterById,
  // PRD.210 — encounter-aware "Add Items from Prescription" action.
  qInvoicePrescriptionCandidates,
  pickerCandidateRowToSelection,
  // PRD.266 — only stock-deducting lines show a Pharmacy/Release badge.
  routesToInvoiceFulfillment,
  // PRD.238 — Print Invoice action. PRD.239 adds WhatsApp delivery sharing
  // the same InvoicePrintLayout source.
  qInvoicePrintSettings,
  qInvoicePaymentsByInvoice,
  qPatientById,
  qPatientContact,
  qAppConfig,
  qAppointmentProviders,
  qUserProviderLink,
  type InvoiceRow,
  type InvoiceItemRow,
  type InvoicePaymentRow,
  type InvoicePrintSettingsRow,
  type AppConfigRow,
  type AppointmentRow,
  type AppointmentServiceRow,
  type AppointmentProviderRow,
  type InventoryPickerCandidateRow,
  type InventoryPickerSelection,
  type InvoiceItemDispenseStatusRow,
  type InvoiceItemProductReleaseStatusRow,
  type InvoicePrescriptionCandidateRow,
  type PatientContactRow,
  type PatientDetailRow,
  type PatientEncounterRow,
  type ProductReleaseBatchOptionRow,
  type UserProviderLinkRow,
  // PRD.264 — Used Supplies billing.
  qReadyForBillingUsedSupplies,
  type UsedSupplyRow,
} from '@helios/data-access';
import { markUsedSupplyBilled } from '../../../services/used-supply-service';
// PRD.238 — Invoice Print runtime action.
import {
  InvoicePrintLayout,
  type InvoicePrintLineItem,
  type InvoicePrintPayment,
} from '../../clinic/invoice-print/InvoicePrintLayout';
import {
  INVOICE_PRINT_STYLES,
  resolveInvoicePrintSettings,
} from '../../clinic/invoice-print/print-utils';
import { formatPatientAge, triggerPrint } from '../../clinic/prescription-print/print-utils';
// PRD.239 — WhatsApp delivery of the same InvoicePrintLayout source.
import { ShareInvoiceModal } from '../../../components/ShareInvoiceModal';
import { Modal } from '@helios/ui/overlay';
import {
  type InvoiceStatus,
  invoiceSummary,
  rowTotal,
  rowDiscountApplied,
  formatDateShort,
} from './consultation-data';
import { useFormatMoney, useRegionalSettings } from '../../../hooks/useFormatMoney';
// PRD.218 — Invoice Payments V1
import { RecordPaymentDialog } from '../../billing/RecordPaymentDialog';
import { NewPaymentDialog } from '../../billing/NewPaymentDialog';
import { PaymentHistoryList } from '../../billing/PaymentHistoryList';
import { InvoiceItemPaymentStatus } from '../../billing/InvoiceItemPaymentStatus';
// PRD.227 — Invoice-scoped credit/debit adjustments
import { InvoiceAdjustmentsList } from '../../billing/InvoiceAdjustmentsList';
import { AddAdjustmentDialog } from '../../billing/AddAdjustmentDialog';

interface InvoicesTabProps {
  patientId: string;
  patientName?: string;
  encounterId?: string;
  visitId?: string;
  initialInvoiceId?: string;
  /** When true, hide the internal "Invoices" title + subtitle */
  hideHeader?: boolean;
  /** When true, use a quiet icon-only button instead of a prominent CTA */
  quietActions?: boolean;
  /** When true, collapse to a single-column (dropdown + detail) layout for narrow containers like drawers */
  singlePane?: boolean;
  /** When true, new invoice line items default to "Add to Treatment Record"
   * checked. Used by Treatment Record Workspace mode (PRD.182) so charges
   * land in the treatment record without a manual toggle. */
  defaultAddToTreatment?: boolean;
}

export function InvoicesTab({
  patientId,
  patientName = '',
  encounterId,
  visitId,
  initialInvoiceId,
  hideHeader,
  quietActions,
  singlePane,
  defaultAddToTreatment = false,
}: InvoicesTabProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#78716c';
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const fmt = useFormatMoney();

  // ─── Queries ───
  // PRD.229 follow-up — when viewing inside an encounter, scope by the
  // encounter's DATE (not encounter_id) so the user sees every invoice
  // the patient has on the same visit day, even if a sibling encounter
  // owns some of them. Fetch the encounter to get its started_at, then
  // derive the date. Falls back to the encounter-id query for the brief
  // moment before the encounter row is available.
  const { data: encLookupRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    encounterId ? qEncounterById(encounterId) : qNoOp('invoice-tab-enc-lookup'),
  );
  const encounterDate = useMemo(() => {
    if (!encounterId) return null;
    const startedAt = encLookupRows[0]?.started_at ?? null;
    return startedAt ? startedAt.slice(0, 10) : null;
  }, [encounterId, encLookupRows]);

  const invoiceQuery = encounterDate
    ? qInvoicesByPatientAndDate(patientId, encounterDate)
    : encounterId
      ? qInvoicesByEncounter(patientId, encounterId)
      : qInvoicesByPatient(patientId);
  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(invoiceQuery);

  const [selectedId, setSelectedId] = useState<string>(initialInvoiceId ?? '');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | InvoiceStatus>('all');
  const [savedToast, setSavedToast] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // PRD.218 — Invoice Payments V1: dialog open state for the selected invoice.
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  // When set, the Record Payment dialog opens in edit mode for this payment.
  const [editPayment, setEditPayment] = useState<InvoicePaymentRow | null>(null);
  // PRD.252 — payment-first ("+ New Payment") dialog, scoped to the whole patient.
  const [newPaymentOpen, setNewPaymentOpen] = useState(false);

  // Tracks an invoice id we JUST inserted so the auto-select useEffect
  // doesn't snap selection back to invoiceRows[0] during the brief
  // window before PowerSync's watched query refreshes with the new row.
  const justCreatedRef = useRef<string | null>(null);

  // Auto-select: prefer initialInvoiceId, then first invoice when list changes
  useEffect(() => {
    if (invoiceRows.length === 0) return;
    // Honor a freshly-created invoice that's still propagating into
    // invoiceRows. Once it shows up we clear the tag and resume normal
    // auto-correct behaviour.
    if (justCreatedRef.current && justCreatedRef.current === selectedId) {
      if (invoiceRows.find((r) => r.id === selectedId)) {
        justCreatedRef.current = null;
      }
      return;
    }
    if (initialInvoiceId && invoiceRows.find((r) => r.id === initialInvoiceId)) {
      setSelectedId(initialInvoiceId);
      return;
    }
    if (!invoiceRows.find((r) => r.id === selectedId)) {
      setSelectedId(invoiceRows[0].id);
    }
  }, [invoiceRows, selectedId, initialInvoiceId]);

  const filtered = useMemo(() => {
    let result = invoiceRows;
    if (statusFilter !== 'all') {
      result = result.filter((inv) => inv.status === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (inv) =>
          (inv.invoice_no ?? '').toLowerCase().includes(q) ||
          (inv.date_iso ?? '').includes(q) ||
          (inv.status ?? '').includes(q),
      );
    }
    return result;
  }, [invoiceRows, search, statusFilter]);

  const selectedInvoice = invoiceRows.find((inv) => inv.id === selectedId) ?? null;

  // ─── Writes ───

  const handleNewInvoice = useCallback(async () => {
    if (!db) return;
    const now = new Date().toISOString();
    const maxNum = invoiceRows.reduce((max, inv) => {
      const n = parseInt((inv.invoice_no ?? '').replace('INV-', ''), 10);
      return n > max ? n : max;
    }, 0);

    const invoiceId = crypto.randomUUID();
    const invoiceNo = `INV-${String(maxNum + 1).padStart(5, '0')}`;

    // Create invoice. New invoices start blank — totals are 0 and no
    // default line item is seeded. Users explicitly add lines via
    // "+ Add item", the line search, or "+ Add Service from Appointment"
    // (PRD.209).
    await db.execute(
      `INSERT INTO invoices
        (id, org_id, clinic_id, patient_id, encounter_id, visit_id,
         invoice_no, patient_name, date_iso, status, notes,
         subtotal, discount_total, tax_total, grand_total, created_at, updated_at)
       VALUES (?, ?, NULL, ?, ?, ?,
         ?, ?, ?, ?, '',
         0, 0, 0, 0, ?, ?)`,
      [
        invoiceId,
        orgId,
        patientId,
        encounterId ?? null,
        visitId ?? null,
        invoiceNo,
        patientName,
        // PRD.229 follow-up — when creating from an encounter context,
        // anchor the invoice's date_iso to the encounter's date so it
        // sits in the same date-scoped list this tab now renders. Falls
        // back to today when no encounter date is known (patient page).
        encounterDate ?? now.slice(0, 10),
        'draft',
        now,
        now,
      ],
    );

    // Tag the just-created id so the auto-select useEffect leaves our
    // selection alone until the watched query catches up.
    justCreatedRef.current = invoiceId;
    setSelectedId(invoiceId);
  }, [db, orgId, patientId, patientName, invoiceRows, encounterId, encounterDate, visitId]);

  const handleSave = useCallback(async () => {
    if (!db || !selectedInvoice) {
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 2000);
      return;
    }
    const now = new Date().toISOString();
    const invoiceId = selectedInvoice.id;
    const invoiceDate = selectedInvoice.date_iso ?? now.split('T')[0];

    // Fetch current items
    const items = await db.getAll<InvoiceItemRow>(
      `SELECT * FROM invoice_items WHERE invoice_id = ?`,
      [invoiceId],
    );

    for (const item of items) {
      const addToTx = item.add_to_treatment === 1;
      const itemTotal = (item.qty ?? 1) * (item.unit_price ?? 0) - (item.discount ?? 0);
      const chargedCents = Math.round(Math.max(0, itemTotal) * 100);

      if (addToTx) {
        // Upsert treatment record for this invoice item
        const existing = await db.getAll<{ id: string }>(
          `SELECT id FROM patient_treatment_records WHERE invoice_item_id = ?`,
          [item.id],
        );
        if (existing.length > 0) {
          // Update
          await db.execute(
            `UPDATE patient_treatment_records SET date = ?, tooth_nos = ?, procedure_name = ?, amount_charged_cents = ?, notes = ?, updated_at = ? WHERE id = ?`,
            [
              invoiceDate,
              item.remarks || null,
              item.description_snapshot ?? item.free_text_name ?? item.service_name ?? '',
              chargedCents,
              item.remarks || null,
              now,
              existing[0].id,
            ],
          );
        } else {
          // Insert
          await db.execute(
            `INSERT INTO patient_treatment_records (id, org_id, clinic_id, patient_id, encounter_id, invoice_id, invoice_item_id, date, tooth_nos, procedure_name, amount_charged_cents, amount_paid_cents, notes, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              crypto.randomUUID(),
              orgId,
              null,
              patientId,
              selectedInvoice.encounter_id ?? null,
              invoiceId,
              item.id,
              invoiceDate,
              item.remarks || null,
              item.description_snapshot ?? item.free_text_name ?? item.service_name ?? '',
              chargedCents,
              0,
              item.remarks || null,
              now,
              now,
            ],
          );
        }
      } else {
        // Remove treatment record if it was created from this invoice item
        await db.execute(`DELETE FROM patient_treatment_records WHERE invoice_item_id = ?`, [
          item.id,
        ]);
      }
    }

    setSavedToast(true);
    setTimeout(() => setSavedToast(false), 2000);
  }, [db, selectedInvoice, orgId, patientId]);

  const handleUpdateNotes = useCallback(
    async (notes: string) => {
      if (!db || !selectedInvoice) return;
      const now = new Date().toISOString();
      await db.execute(`UPDATE invoices SET notes = ?, updated_at = ? WHERE id = ?`, [
        notes,
        now,
        selectedInvoice.id,
      ]);

      // Mirror invoice notes into patient_notes so the entry surfaces on
      // the Patient Timeline and Treatment Record Notes column. Uses a
      // deterministic note id keyed off the invoice id so editing the
      // invoice notes updates the same row instead of creating duplicates.
      // Cleared notes remove the mirrored row.
      const noteId = `invoice-note:${selectedInvoice.id}`;
      const trimmed = notes.trim();
      if (!trimmed) {
        await db.execute(`DELETE FROM patient_notes WHERE id = ?`, [noteId]);
      } else {
        const title = `Invoice ${selectedInvoice.invoice_no ?? ''}`.trim();
        const existing = (await db.getAll<{ id: string }>(
          `SELECT id FROM patient_notes WHERE id = ?`,
          [noteId],
        )) as { id: string }[];
        if (existing.length > 0) {
          await db.execute(
            `UPDATE patient_notes SET title = ?, body = ?, encounter_id = ?, visit_id = ?, updated_at = ? WHERE id = ?`,
            [
              title,
              notes,
              selectedInvoice.encounter_id ?? null,
              selectedInvoice.visit_id ?? null,
              now,
              noteId,
            ],
          );
        } else {
          await db.execute(
            `INSERT INTO patient_notes
              (id, org_id, clinic_id, patient_id, encounter_id, visit_id,
               appointment_id, queue_id,
               title, body, note_type, alert_level, visibility,
               author_user_id, author_name,
               created_at, updated_at)
             VALUES (?, ?, ?, ?, ?, ?, NULL, NULL, ?, ?, 'general', 'none', 'team',
                     NULL, NULL, ?, ?)`,
            [
              noteId,
              orgId,
              null,
              patientId,
              selectedInvoice.encounter_id ?? null,
              selectedInvoice.visit_id ?? null,
              title,
              notes,
              now,
              now,
            ],
          );
        }
      }
    },
    [db, selectedInvoice, orgId, patientId],
  );

  const handleUpdateReferenceNo = useCallback(
    async (refNo: string) => {
      if (!db || !selectedInvoice) return;
      const now = new Date().toISOString();
      await db.execute(`UPDATE invoices SET reference_no = ?, updated_at = ? WHERE id = ?`, [
        refNo || null,
        now,
        selectedInvoice.id,
      ]);
    },
    [db, selectedInvoice],
  );

  const handleUpdateReferenceType = useCallback(
    async (type: string) => {
      if (!db || !selectedInvoice) return;
      const now = new Date().toISOString();
      await db.execute(`UPDATE invoices SET reference_type = ?, updated_at = ? WHERE id = ?`, [
        type,
        now,
        selectedInvoice.id,
      ]);
    },
    [db, selectedInvoice],
  );

  const handleUpdateDate = useCallback(
    async (dateIso: string) => {
      if (!db || !selectedInvoice || !dateIso) return;
      const now = new Date().toISOString();
      await db.execute(`UPDATE invoices SET date_iso = ?, updated_at = ? WHERE id = ?`, [
        dateIso,
        now,
        selectedInvoice.id,
      ]);
    },
    [db, selectedInvoice],
  );

  const handleDeleteInvoice = useCallback(
    async (opts: { deletePayments: boolean; deleteTreatmentRecords: boolean }) => {
      if (!db || !selectedInvoice) return;
      setIsDeleting(true);
      try {
        await deleteInvoiceCascade({
          db,
          invoiceId: selectedInvoice.id,
          deletePayments: opts.deletePayments,
          // Treatment records auto-created from invoice items are owned by the
          // invoice (they link back to it and can't be edited from the
          // Treatment Record view), so deleting the invoice always removes them.
          deleteTreatmentRecords: true,
        });
        setDeleteModalOpen(false);
        setSelectedId('');
      } catch (err) {
        console.error('[DeleteInvoice] Failed:', err);
      } finally {
        setIsDeleting(false);
      }
    },
    [db, selectedInvoice],
  );

  // Check if selected invoice has payments (for modal checkboxes)
  const selectedHasPayments = selectedInvoice?.status === 'paid';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)', flex: 1 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        {!hideHeader ? (
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Invoices
            </h2>
            <p
              style={{
                margin: '2px 0 0',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
              }}
            >
              Manage invoices related to consultations
            </p>
          </div>
        ) : (
          <div />
        )}
        {/* In singlePane mode the dropdown row below already exposes a "+"
            for creating a new invoice, so suppressing this header one
            avoids the duplicate the user noticed. */}
        {singlePane ? null : quietActions ? (
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <button
              onClick={() => setNewPaymentOpen(true)}
              title="New Payment"
              style={{
                width: 30,
                height: 30,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <NavIcon name="peso" />
            </button>
            <button
              onClick={handleNewInvoice}
              title="New Invoice"
              style={{
                width: 30,
                height: 30,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <NavIcon name="plus" />
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <Button variant="outline" size="md" onClick={() => setNewPaymentOpen(true)}>
              + New Payment
            </Button>
            <Button variant="gradient" size="md" onClick={handleNewInvoice}>
              + New Invoice
            </Button>
          </div>
        )}
      </div>

      {/* Split view */}
      <div
        style={
          singlePane
            ? {
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-3)',
                flex: 1,
                minHeight: 0,
              }
            : { display: 'flex', gap: 'var(--sp-4)', flex: 1, minHeight: 0 }
        }
      >
        {/* Single-pane: horizontal pill tabs (one per invoice) + "+" button.
            Replaces the prior <select> dropdown so all invoices are visible
            at a glance and the active one is obvious. Wraps onto multiple
            rows when there are many invoices. */}
        {singlePane && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--sp-2)',
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {invoiceRows.length === 0 ? (
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-muted)',
                    fontStyle: 'italic',
                    padding: '6px 2px',
                  }}
                >
                  No invoices yet
                </span>
              ) : (
                invoiceRows.map((inv) => {
                  const active = inv.id === selectedId;
                  const status = (inv.status ?? 'draft') as InvoiceStatus;
                  const statusTone =
                    INVOICE_PILL_STATUS_STYLES[status] ?? INVOICE_PILL_STATUS_STYLES.draft;
                  const totalLabel = inv.grand_total != null ? fmt(inv.grand_total) : '—';
                  return (
                    <button
                      key={inv.id}
                      type="button"
                      onClick={() => setSelectedId(inv.id)}
                      title={`${inv.invoice_no ?? 'Draft'} · ${status} · ${totalLabel}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        height: 30,
                        padding: '0 10px',
                        borderRadius: 'var(--radius-full)',
                        border: active
                          ? '1px solid var(--color-primary)'
                          : '1px solid var(--border)',
                        background: active
                          ? 'var(--color-primary-bg, #eef2ff)'
                          : 'var(--bg-surface)',
                        color: active ? 'var(--color-primary, #4f46e5)' : 'var(--text)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: active
                          ? 'var(--font-weight-semi)'
                          : 'var(--font-weight-medium)',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      <span>{inv.invoice_no ?? 'Draft'}</span>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0 6px',
                          borderRadius: 'var(--radius-full)',
                          background: statusTone.bg,
                          color: statusTone.color,
                          fontSize: 10,
                          fontWeight: 500,
                          textTransform: 'capitalize',
                          lineHeight: '16px',
                        }}
                      >
                        {status}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
            <button
              onClick={() => setNewPaymentOpen(true)}
              title="New Payment"
              style={{
                width: 30,
                height: 30,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <NavIcon name="peso" />
            </button>
            <button
              onClick={handleNewInvoice}
              title="New Invoice"
              style={{
                width: 30,
                height: 30,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <NavIcon name="plus" />
            </button>
          </div>
        )}
        {/* Left: Invoice List — hidden in single-pane */}
        <div
          style={{
            width: 280,
            flexShrink: 0,
            display: singlePane ? 'none' : 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
          }}
        >
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <div
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-muted)',
                display: 'flex',
                pointerEvents: 'none',
              }}
            >
              <NavIcon name="search" />
            </div>
            <input
              type="text"
              placeholder="Search invoice #, date, status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%',
                height: 'var(--input-height)',
                padding: '0 var(--input-padding-x) 0 40px',
                borderRadius: 'var(--input-radius)',
                border: '1px solid var(--border-input)',
                background: 'var(--input-bg)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--input-text)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | InvoiceStatus)}
            style={selectStyle}
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="issued">Issued</option>
            <option value="paid">Paid</option>
          </select>

          {/* List */}
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1, overflowY: 'auto' }}
          >
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: 'var(--sp-6)',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--sp-3)',
                }}
              >
                <span>No invoices found</span>
                {quietActions && (
                  <button
                    onClick={handleNewInvoice}
                    style={{
                      height: 32,
                      padding: '0 14px',
                      borderRadius: 'var(--radius-md)',
                      border: `1px solid ${primary}`,
                      background: 'transparent',
                      color: primary,
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                    }}
                  >
                    <NavIcon name="plus" /> New Invoice
                  </button>
                )}
              </div>
            ) : (
              filtered.map((inv) => {
                const isSelected = inv.id === selectedId;
                return (
                  <button
                    key={inv.id}
                    type="button"
                    onClick={() => setSelectedId(inv.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 'var(--sp-3) var(--sp-4)',
                      border: 'none',
                      borderLeft: isSelected ? `3px solid ${primary}` : '3px solid transparent',
                      background: isSelected ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-semi)',
                          color: 'var(--text)',
                        }}
                      >
                        {inv.invoice_no}
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                          marginTop: 2,
                        }}
                      >
                        {inv.date_iso ? formatDateShort(inv.date_iso) : ''}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 4,
                      }}
                    >
                      <StatusPill status={(inv.status as InvoiceStatus) ?? 'draft'} />
                      <span
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--text)',
                        }}
                      >
                        {fmt(inv.grand_total ?? 0)}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right: Invoice Detail */}
        <div
          style={{
            flex: 1,
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--bg-surface)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {selectedInvoice ? (
            <InvoiceDetail
              invoice={selectedInvoice}
              orgId={orgId}
              primary={primary}
              onRecordPayment={() => {
                setEditPayment(null);
                setPaymentDialogOpen(true);
              }}
              onEditPayment={(p) => {
                setEditPayment(p);
                setPaymentDialogOpen(true);
              }}
              onSave={handleSave}
              onUpdateNotes={handleUpdateNotes}
              onUpdateReferenceNo={handleUpdateReferenceNo}
              onUpdateReferenceType={handleUpdateReferenceType}
              onUpdateDate={handleUpdateDate}
              onDelete={() => setDeleteModalOpen(true)}
              defaultAddToTreatment={defaultAddToTreatment}
            />
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              Select an invoice to view details
            </div>
          )}
        </div>
      </div>

      {/* Saved toast */}
      {savedToast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            padding: 'var(--sp-3) var(--sp-5)',
            background: '#065f46',
            color: '#fff',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 100,
          }}
        >
          Saved
        </div>
      )}

      <DeleteInvoiceModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteInvoice}
        invoiceNo={selectedInvoice?.invoice_no ?? ''}
        isPaid={selectedInvoice?.status === 'paid'}
        hasPayments={selectedHasPayments}
        hasTreatmentRecords={false}
        isDeleting={isDeleting}
      />

      {/* PRD.218 — Record Payment dialog (replaces "Mark as Paid" shortcut).
          PRD.229 — invoice-locked here (we always have a selectedInvoice). */}
      <RecordPaymentDialog
        open={paymentDialogOpen && !!selectedInvoice}
        onOpenChange={(o) => {
          setPaymentDialogOpen(o);
          if (!o) setEditPayment(null);
        }}
        invoice={
          selectedInvoice
            ? {
                id: selectedInvoice.id,
                invoice_no: selectedInvoice.invoice_no,
                grand_total: selectedInvoice.grand_total,
                status: selectedInvoice.status,
              }
            : null
        }
        patientId={patientId}
        orgId={orgId}
        editPayment={editPayment}
      />

      {/* PRD.252 — payment-first entry across all the patient's outstanding
          invoices (distinct from the invoice-locked dialog above). */}
      <NewPaymentDialog
        open={newPaymentOpen}
        onOpenChange={setNewPaymentOpen}
        patientId={patientId}
        orgId={orgId}
      />
    </div>
  );
}

// ─── Invoice Detail Panel ───

function InvoiceDetail({
  invoice,
  orgId,
  primary,
  onRecordPayment,
  onEditPayment,
  onSave,
  onUpdateNotes,
  onUpdateReferenceNo,
  onUpdateReferenceType,
  onUpdateDate,
  onDelete,
  defaultAddToTreatment = false,
}: {
  invoice: InvoiceRow;
  orgId: string;
  primary: string;
  /** PRD.218 — Opens the Record Payment dialog. Replaces the old onMarkPaid shortcut. */
  onRecordPayment: () => void;
  /** Opens the Record Payment dialog in edit mode for an existing payment. */
  onEditPayment: (payment: InvoicePaymentRow) => void;
  onSave: () => void;
  onUpdateNotes: (notes: string) => void;
  onUpdateReferenceNo: (refNo: string) => void;
  onUpdateReferenceType: (type: string) => void;
  /** Persist the invoice date (date_iso, YYYY-MM-DD). */
  onUpdateDate: (dateIso: string) => void;
  onDelete: () => void;
  defaultAddToTreatment?: boolean;
}) {
  const { db } = usePowerSyncDb();
  const fmt = useFormatMoney();
  const regional = useRegionalSettings();
  const currencySymbol = regional.money_format.currency_symbol;
  // PRD.206 — needed to attribute released_by on the release header.
  const { user } = useAuth();
  const releaseUserId = user?.uid ?? '';
  const releaseUserName = user?.displayName ?? user?.email ?? '';
  const brandTokens = useBrandTokens();
  const { data: itemRows = [] } = usePowerSyncQuery<InvoiceItemRow>(qInvoiceItems(invoice.id));
  const { data: activeServices = [] } = usePowerSyncQuery<AppointmentServiceRow>(
    qActiveAppointmentServices(),
  );
  // PRD.196 — derived dispense status per invoice line. Empty until the
  // line has a confirmed pharmacy_dispense_lines row; updates live when
  // Pharmacy confirms a dispense for any line on this invoice.
  const { data: dispenseStatusRows = [] } = usePowerSyncQuery<InvoiceItemDispenseStatusRow>(
    qInvoiceItemDispenseStatuses(invoice.id),
  );
  const dispensedByItem = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of dispenseStatusRows) map.set(r.invoice_item_id, r.total_dispensed ?? 0);
    return map;
  }, [dispenseStatusRows]);

  // PRD.206 / PRD.234 — derived release status per invoice line. Returns
  // one row per invoice item with the joined inventory item type, the
  // `dispense_via_pharmacy` capability flag, and the total released qty.
  // PRD.234: a Product with `dispense_via_pharmacy = 1` follows the
  // Pharmacy workflow (DispenseStatusBadge), so the consumer treats it
  // as non-Release even though item_type is 'product'.
  const { data: releaseStatusRows = [] } = usePowerSyncQuery<InvoiceItemProductReleaseStatusRow>(
    qInvoiceProductReleaseStatuses(invoice.id),
  );
  const releaseStatusByItem = useMemo(() => {
    const map = new Map<
      string,
      { itemType: string | null; totalReleased: number; dispenseViaPharmacy: boolean }
    >();
    for (const r of releaseStatusRows) {
      map.set(r.invoice_item_id, {
        itemType: r.item_type,
        totalReleased: r.total_released ?? 0,
        dispenseViaPharmacy: r.dispense_via_pharmacy === 1,
      });
    }
    return map;
  }, [releaseStatusRows]);

  const isInvoicePaid = invoice.status === 'paid';

  // ── PRD.238 — Print Invoice payload ───────────────────────────────────────
  // The Print Invoice action uses the org-default invoice print settings,
  // falling back to app_config branding when the wizard hasn't been run yet.
  // Doctor / provider name comes from the appointment provider linked to the
  // invoice's encounter (when available), otherwise the printing user's
  // linked provider. Patient + payments are queried so the printout has
  // everything it needs.
  const orgIdForPrint = orgId;
  const userId = user?.uid ?? '';
  const { data: invoicePrintSettingsRows = [] } =
    usePowerSyncQuery<InvoicePrintSettingsRow>(qInvoicePrintSettings());
  const { data: invoicePrintAppConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const { data: invoicePaymentRows = [] } = usePowerSyncQuery<InvoicePaymentRow>(
    qInvoicePaymentsByInvoice(invoice.id),
  );
  const { data: invoicePatientRows = [] } = usePowerSyncQuery<PatientDetailRow>(
    qPatientById(invoice.patient_id ?? ''),
  );
  // PRD.239 — patient contact for WhatsApp delivery (display + opt-in lookup).
  const { data: invoicePatientContactRows = [] } = usePowerSyncQuery<PatientContactRow>(
    qPatientContact(invoice.patient_id ?? ''),
  );
  const { data: invoiceProviderRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: invoiceUserProviderLinkRows = [] } = usePowerSyncQuery<UserProviderLinkRow>(
    qUserProviderLink(orgIdForPrint, userId),
  );
  const { data: invoiceEncounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    invoice.encounter_id ? qEncounterById(invoice.encounter_id) : qNoOp('invoice-print-encounter'),
  );

  const printSettings = useMemo(
    () =>
      resolveInvoicePrintSettings(
        invoicePrintSettingsRows[0] ?? null,
        invoicePrintAppConfigRows[0] ?? null,
      ),
    [invoicePrintSettingsRows, invoicePrintAppConfigRows],
  );

  const printPatient = useMemo(() => {
    const p = invoicePatientRows[0];
    return {
      name: p?.full_name ?? invoice.patient_name ?? '',
      age: formatPatientAge(p?.birth_date),
      sex: p?.sex ?? '',
      patientNumber: p?.patient_number ?? '',
    };
  }, [invoicePatientRows, invoice.patient_name]);

  const printProviderName = useMemo(() => {
    // Prefer the provider linked to the invoice's encounter via appointment.
    const enc = invoiceEncounterRows[0];
    const encProviderId = enc?.provider_id ?? null;
    if (encProviderId) {
      const p = invoiceProviderRows.find((r) => r.id === encProviderId);
      const name = p?.display_name?.trim() || p?.full_name?.trim();
      if (name) return name;
    }
    // Fallback: the printing user's linked provider.
    const linkedId = invoiceUserProviderLinkRows[0]?.provider_id ?? null;
    if (linkedId) {
      const p = invoiceProviderRows.find((r) => r.id === linkedId);
      const name = p?.display_name?.trim() || p?.full_name?.trim();
      if (name) return name;
    }
    return user?.displayName ?? '';
  }, [invoiceEncounterRows, invoiceProviderRows, invoiceUserProviderLinkRows, user?.displayName]);

  // Payments-derived totals (used both by the runtime print payload below
  // and by any UI summary in the InvoiceDetail body).
  const invoicePaymentsTotal = useMemo(
    () => invoicePaymentRows.reduce((s, p) => s + (p.amount_cents ?? 0) / 100, 0),
    [invoicePaymentRows],
  );

  // PRD.209 — eligibility for "Add Service from Appointment".
  //
  // Primary path: invoice.encounter_id → encounter.appointment_id →
  // appointment.service_id → appointment_services row. This is the
  // "explicit link" the PRD describes (encounter started from an
  // appointment via Start Encounter).
  //
  // Fallback path (common in practice): when the encounter was started
  // independently (e.g. via Continue Visit Note from the patient page)
  // it has no appointment_id even when the patient also has an
  // appointment booked the same day. To bridge that, when the explicit
  // link is missing we look at all appointments for this patient and
  // pick a single same-date appointment with a service_id. Multiple or
  // zero matches → action stays hidden (we don't disambiguate
  // automatically; the user can still use the line search).
  const linkedEncounterId = invoice.encounter_id ?? '';
  const { data: linkedEncounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncounterById(linkedEncounterId),
  );
  const linkedEncounter = linkedEncounterId ? (linkedEncounterRows[0] ?? null) : null;

  // Date-only comparison: encounter.started_at is an ISO timestamp, so
  // slice the YYYY-MM-DD prefix; appointment.date is already YYYY-MM-DD.
  // Falls back to invoice.date_iso when started_at is missing (encounter
  // hadn't been started yet) so a same-day draft still works.
  const consultationDate = useMemo(() => {
    const raw = linkedEncounter?.started_at ?? invoice.date_iso ?? null;
    return raw ? raw.slice(0, 10) : null;
  }, [linkedEncounter?.started_at, invoice.date_iso]);

  const explicitAppointmentId = linkedEncounter?.appointment_id ?? '';
  const { data: explicitAppointmentRows = [] } = usePowerSyncQuery<AppointmentRow>(
    qAppointmentById(explicitAppointmentId),
  );
  const explicitAppointment = explicitAppointmentId ? (explicitAppointmentRows[0] ?? null) : null;

  // Fallback: same-patient same-date appointments. Only consulted when
  // the explicit link is absent. Returns ALL appointments for the
  // patient (the table is small per-patient), filtered client-side.
  const fallbackPatientId = !explicitAppointment ? (invoice.patient_id ?? '') : '';
  const { data: patientAppointmentRows = [] } = usePowerSyncQuery<AppointmentRow>(
    qAppointmentsByPatient(fallbackPatientId),
  );
  const fallbackAppointment = useMemo(() => {
    if (explicitAppointment) return null;
    if (!consultationDate) return null;
    const sameDay = patientAppointmentRows.filter(
      (a) => a.date === consultationDate && a.service_id,
    );
    return sameDay.length === 1 ? sameDay[0] : null;
  }, [explicitAppointment, consultationDate, patientAppointmentRows]);

  const linkedAppointment = explicitAppointment ?? fallbackAppointment;
  const linkedServiceId = linkedAppointment?.service_id ?? '';
  const { data: linkedServiceRows = [] } = usePowerSyncQuery<AppointmentServiceRow>(
    qAppointmentServiceById(linkedServiceId),
  );
  const linkedAppointmentService = linkedServiceId ? (linkedServiceRows[0] ?? null) : null;

  const appointmentDate = linkedAppointment?.date ?? null;
  const datesMatch =
    !!consultationDate && !!appointmentDate && consultationDate === appointmentDate;

  // PRD.210 — "Add Items from Prescription" candidate fetch. Inventory-
  // bound prescription lines for the current visit, joined with the
  // inventory_items row for live pricing and item type. We only run the
  // query when both ids are present (otherwise the helper returns an
  // empty list anyway, but skipping avoids a wasted watch).
  const prescriptionCandidatePatientId = invoice.patient_id ?? '';
  const prescriptionCandidateEncounterId = invoice.encounter_id ?? '';
  const { data: prescriptionCandidateRows = [] } =
    usePowerSyncQuery<InvoicePrescriptionCandidateRow>(
      qInvoicePrescriptionCandidates(
        prescriptionCandidatePatientId,
        prescriptionCandidateEncounterId,
      ),
    );
  const [prescriptionModalOpen, setPrescriptionModalOpen] = useState(false);

  // PRD.206 — Product Release modal state. Opened from the Release button
  // on a paid Product invoice line; the user picks an existing batch (if
  // applicable) and confirms. Confirm writes product_releases +
  // product_release_lines + an outbound stock_movements row. Invoice
  // save/payment alone never touches stock.
  const [releaseModalLineId, setReleaseModalLineId] = useState<string | null>(null);
  const [releaseSavingItemId, setReleaseSavingItemId] = useState<string | null>(null);
  const [releaseError, setReleaseError] = useState<{ id: string; text: string } | null>(null);

  // Write path for Confirm Product Release. Mirrors Pharmacy Confirm
  // Dispense (PRD.194) — three inserts: header, line, outbound
  // stock_movements row. Caller passes the chosen batch (or null when
  // the product has none) and the quantity to release.
  const confirmProductRelease = useCallback(
    async (args: {
      invoiceItemId: string;
      inventoryItemId: string;
      qtyToRelease: number;
      itemNameSnapshot: string;
      skuSnapshot: string | null;
      unitSnapshot: string | null;
      batch: ProductReleaseBatchOptionRow | null;
    }) => {
      const {
        invoiceItemId,
        inventoryItemId,
        qtyToRelease,
        itemNameSnapshot,
        skuSnapshot,
        unitSnapshot,
        batch,
      } = args;
      if (!db) {
        setReleaseError({ id: invoiceItemId, text: 'Local database not ready.' });
        return;
      }
      if (!orgId) {
        setReleaseError({ id: invoiceItemId, text: 'Missing organization context.' });
        return;
      }
      if (qtyToRelease <= 0) {
        setReleaseError({
          id: invoiceItemId,
          text: 'No remaining quantity to release.',
        });
        return;
      }
      if (batch && batch.available_quantity != null && qtyToRelease > batch.available_quantity) {
        setReleaseError({
          id: invoiceItemId,
          text: `Selected batch only has ${batch.available_quantity} available.`,
        });
        return;
      }

      setReleaseSavingItemId(invoiceItemId);
      setReleaseError(null);
      try {
        const releaseId = crypto.randomUUID();
        const lineId = crypto.randomUUID();
        const now = new Date().toISOString();
        const releaseNumber = `REL-${now.slice(0, 10)}-${releaseId.slice(0, 4)}`;

        // 1) Header — anchored to the invoice we're on; patient_id /
        //    encounter_id flow through from the invoice for traceability.
        await db.execute(
          `INSERT INTO product_releases (
            id, org_id, release_number, patient_id, encounter_id, invoice_id,
            status, released_at, released_by, remarks,
            created_at, updated_at
          ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            releaseId,
            orgId,
            releaseNumber,
            invoice.patient_id ?? null,
            invoice.encounter_id ?? null,
            invoice.id,
            'confirmed',
            now,
            releaseUserName || releaseUserId || null,
            null,
            now,
            now,
          ],
        );

        // 2) Line
        await db.execute(
          `INSERT INTO product_release_lines (
            id, org_id, product_release_id, invoice_item_id,
            inventory_item_id, inventory_batch_id,
            item_name_snapshot, sku_snapshot, batch_number_snapshot,
            expiry_date_snapshot, unit_snapshot, instructions_snapshot,
            quantity_to_release, quantity_released,
            status, remarks, created_at, updated_at
          ) VALUES (
            ?, ?, ?, ?,
            ?, ?,
            ?, ?, ?,
            ?, ?, ?,
            ?, ?,
            ?, ?, ?, ?
          )`,
          [
            lineId,
            orgId,
            releaseId,
            invoiceItemId,
            inventoryItemId,
            batch?.inventory_batch_id ?? null,
            itemNameSnapshot,
            skuSnapshot,
            batch?.batch_number ?? null,
            batch?.expiry_date ?? null,
            unitSnapshot,
            null,
            qtyToRelease,
            qtyToRelease,
            'confirmed',
            null,
            now,
            now,
          ],
        );

        // 3) Outbound stock movement — the actual stock-out event for
        //    Product Release. Distinct source_type from pharmacy_dispense
        //    so Stock Card / future returns can resolve it correctly.
        await db.execute(
          `INSERT INTO stock_movements (
            id, org_id, inventory_item_id, inventory_batch_id,
            source_type, source_id, source_line_id,
            movement_type, quantity_in, quantity_out, unit,
            movement_date, remarks, created_by, created_at, updated_at
          ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            orgId,
            inventoryItemId,
            batch?.inventory_batch_id ?? null,
            'product_release',
            releaseId,
            lineId,
            'product_release',
            0,
            qtyToRelease,
            unitSnapshot,
            now.slice(0, 10),
            null,
            releaseUserId || null,
            now,
            now,
          ],
        );

        setReleaseSavingItemId(null);
        setReleaseModalLineId(null);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Release failed. Please try again.';
        setReleaseSavingItemId(null);
        setReleaseError({ id: invoiceItemId, text: msg });
      }
    },
    [
      db,
      orgId,
      invoice.id,
      invoice.patient_id,
      invoice.encounter_id,
      releaseUserId,
      releaseUserName,
    ],
  );

  // ── Local item state (only written to DB on Save) ──

  type UiItem = {
    id: string;
    serviceName: string;
    serviceId: string | null;
    qty: number;
    unitPrice: number;
    discount: number;
    /** PRD.228 — 'fixed' (peso amount) or 'percentage' (% of row subtotal). */
    discountType: 'fixed' | 'percentage';
    taxRate: number;
    remarks: string;
    addToTreatment: boolean;
    // PRD.190 — Optional Medicine inventory reference + invoice snapshots.
    // When inventoryItemId is set, snapshot fields hold the catalog values
    // captured at the time of invoicing. Free-text-only lines leave
    // inventoryItemId null and may set freeTextName.
    inventoryItemId: string | null;
    freeTextName: string | null;
    descriptionSnapshot: string | null;
    skuSnapshot: string | null;
    unitSnapshot: string | null;
    unitPriceSnapshot: number | null;
    taxInclusiveSnapshot: boolean | null;
    openPricingSnapshot: boolean | null;
    sourceType: string | null; // 'manual' | 'inventory_item' | ...
    sourceId: string | null;
    _isNew?: boolean; // local-only: needs INSERT on save
  };

  const toUiItems = (rows: InvoiceItemRow[]): UiItem[] =>
    rows.map((r) => ({
      id: r.id,
      serviceName: r.service_name ?? '',
      serviceId: r.service_id ?? null,
      qty: r.qty ?? 1,
      unitPrice: r.unit_price ?? 0,
      discount: r.discount ?? 0,
      // PRD.228 — legacy rows have no discount_type; treat as 'fixed' so
      // their stored peso amount keeps its original meaning.
      discountType: r.discount_type === 'percentage' ? 'percentage' : 'fixed',
      taxRate: r.tax_rate ?? 0,
      remarks: r.remarks ?? '',
      addToTreatment: r.add_to_treatment === 1,
      inventoryItemId: r.inventory_item_id ?? null,
      freeTextName: r.free_text_name ?? null,
      descriptionSnapshot: r.description_snapshot ?? null,
      skuSnapshot: r.sku_snapshot ?? null,
      unitSnapshot: r.unit_snapshot ?? null,
      unitPriceSnapshot: r.unit_price_snapshot ?? null,
      taxInclusiveSnapshot:
        r.tax_inclusive_snapshot == null ? null : r.tax_inclusive_snapshot === 1,
      openPricingSnapshot: r.open_pricing_snapshot == null ? null : r.open_pricing_snapshot === 1,
      sourceType: r.source_type ?? null,
      sourceId: r.source_id ?? null,
    }));

  const [localItems, setLocalItems] = useState<UiItem[]>(() => toUiItems(itemRows));
  // Ids of persisted items the user removed locally; deleted on Save.
  const [removedItemIds, setRemovedItemIds] = useState<string[]>([]);
  // PRD.261 — reasons any package line was skipped during patient-package
  // creation on save (e.g. package has no included items).
  const [packageSaleNotice, setPackageSaleNotice] = useState<string[]>([]);

  // Sync from DB when itemRows change externally (e.g. after save round-trips)
  const lastSyncRef = useRef<string>('');
  useEffect(() => {
    const key = itemRows.map((r) => `${r.id}:${r.updated_at}`).join(',');
    if (key !== lastSyncRef.current) {
      lastSyncRef.current = key;
      setLocalItems(toUiItems(itemRows));
      // A DB round-trip (save or invoice switch) is a clean slate — any pending
      // local removals have either been flushed or belong to another invoice.
      setRemovedItemIds([]);
    }
  }, [itemRows]);

  // ── Local notes state — only persisted on Save click. Mirrors the
  // local-only edit pattern used for invoice items so typing stays
  // snappy and the user controls when the row + mirrored patient_note
  // get written.
  const [localNotes, setLocalNotes] = useState<string>(invoice.notes ?? '');

  // PRD.227 — Invoice-linked adjustment dialog state.
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);

  // PRD.229 §6 / AC #6+#7 — Inner Payments / Adjustments tabs removed;
  // those sections are now rendered inline below the invoice editor so
  // the workflows stay visible whenever an invoice is selected.
  const lastNotesInvoiceRef = useRef<string>(invoice.id);
  const lastNotesServerRef = useRef<string>(invoice.notes ?? '');
  useEffect(() => {
    // When switching invoices, or when the server value changes from
    // outside this editor (after save round-trip), pull it in. Don't
    // clobber local edits while the user is still on the same invoice.
    const switchedInvoice = lastNotesInvoiceRef.current !== invoice.id;
    const server = invoice.notes ?? '';
    if (switchedInvoice || server !== lastNotesServerRef.current) {
      setLocalNotes(server);
      lastNotesInvoiceRef.current = invoice.id;
      lastNotesServerRef.current = server;
    }
  }, [invoice.id, invoice.notes]);

  const uiItems = localItems;
  const summary = invoiceSummary(uiItems);

  const handleUpdateItem = useCallback(
    (itemId: string, patch: Partial<Omit<UiItem, 'id' | '_isNew'>>) => {
      setLocalItems((prev) =>
        prev.map((item) => (item.id === itemId ? { ...item, ...patch } : item)),
      );
    },
    [],
  );

  const handleAddItem = useCallback(() => {
    const id = crypto.randomUUID();
    setLocalItems((prev) => [
      ...prev,
      {
        id,
        serviceName: '',
        serviceId: null,
        qty: 1,
        unitPrice: 0,
        discount: 0,
        discountType: 'fixed',
        taxRate: 0,
        remarks: '',
        addToTreatment: defaultAddToTreatment,
        inventoryItemId: null,
        freeTextName: null,
        descriptionSnapshot: null,
        skuSnapshot: null,
        unitSnapshot: null,
        unitPriceSnapshot: null,
        taxInclusiveSnapshot: null,
        openPricingSnapshot: null,
        sourceType: null,
        sourceId: null,
        _isNew: true,
      },
    ]);
  }, [defaultAddToTreatment]);

  // Remove a line item. New (unsaved) lines just drop from local state;
  // persisted lines are tracked so Save deletes them (and any treatment
  // record auto-created from them) from the DB.
  const handleRemoveItem = useCallback((itemId: string) => {
    setLocalItems((prev) => {
      const target = prev.find((i) => i.id === itemId);
      if (target && !target._isNew) {
        setRemovedItemIds((ids) => (ids.includes(itemId) ? ids : [...ids, itemId]));
      }
      return prev.filter((i) => i.id !== itemId);
    });
  }, []);

  // PRD.209 — Add the appointment's selected service as an invoice line.
  // Pure UI add (mirrors handleAddItem); the new line gets persisted on
  // the next Save, same as any other manually-added line. No stock
  // movement is touched. Eligibility (encounter linked, appointment has
  // service, dates match) is enforced by the calling button so this
  // function is intentionally unconditional once invoked.
  const handleAddAppointmentService = useCallback(() => {
    if (!linkedAppointment?.service_id || !linkedAppointmentService) return;
    const serviceId = linkedAppointment.service_id;
    const serviceName = linkedAppointmentService.name ?? '';
    const unitPrice =
      linkedAppointmentService.price_cents != null ? linkedAppointmentService.price_cents / 100 : 0;
    setLocalItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        serviceName,
        serviceId,
        qty: 1,
        unitPrice,
        discount: 0,
        discountType: 'fixed',
        taxRate: 0,
        remarks: '',
        addToTreatment: defaultAddToTreatment,
        inventoryItemId: null,
        freeTextName: null,
        descriptionSnapshot: null,
        skuSnapshot: null,
        unitSnapshot: null,
        unitPriceSnapshot: null,
        taxInclusiveSnapshot: null,
        openPricingSnapshot: null,
        sourceType: 'manual',
        sourceId: null,
        _isNew: true,
      },
    ]);
  }, [defaultAddToTreatment, linkedAppointment?.service_id, linkedAppointmentService]);

  // PRD.210 — Add the selected prescription rows as invoice lines.
  // Pure UI add, mirrors handleAddItem / handleAddAppointmentService.
  // The new lines persist on next Save. NO stock movement is touched —
  // Pharmacy Confirm Dispense / Product Release stay the only stock-out
  // events. source_type='prescription' + source_id=prescription_id is
  // the duplicate key (also surfaced in the modal so already-added
  // items can be greyed out).
  const handleAddPrescriptionItems = useCallback(
    (rows: InvoicePrescriptionCandidateRow[]) => {
      if (rows.length === 0) return;
      const additions: UiItem[] = rows.map((r) => {
        const displayName =
          r.item_name_snapshot?.trim() || r.free_text_name?.trim() || r.item_name?.trim() || '';
        const unitPrice = r.inventory_selling_price ?? 0;
        return {
          id: crypto.randomUUID(),
          serviceName: displayName,
          serviceId: null,
          qty: 1,
          unitPrice,
          discount: 0,
          discountType: 'fixed',
          taxRate: 0,
          remarks: '',
          addToTreatment: defaultAddToTreatment,
          inventoryItemId: r.inventory_item_id,
          freeTextName: null,
          descriptionSnapshot: displayName || null,
          skuSnapshot: r.sku_snapshot,
          unitSnapshot: r.unit_snapshot ?? r.inventory_unit ?? null,
          unitPriceSnapshot: r.inventory_selling_price ?? null,
          taxInclusiveSnapshot: null,
          openPricingSnapshot: null,
          sourceType: 'prescription',
          sourceId: r.prescription_id,
          _isNew: true,
        };
      });
      setLocalItems((prev) => [...prev, ...additions]);
    },
    [defaultAddToTreatment],
  );

  // PRD.264 — Add ready-for-billing Used Supplies as invoice lines. Stock was
  // already deducted at usage time, so these lines never deduct again. On save,
  // flushItemsToDb marks each used supply billed and links the invoice line.
  const handleAddUsedSupplyItems = useCallback(
    (rows: UsedSupplyRow[]) => {
      if (rows.length === 0) return;
      const additions: UiItem[] = rows.map((r) => {
        const name = r.item_name_snapshot?.trim() || 'Supply';
        const unitPrice = r.unit_price_snapshot ?? 0;
        return {
          id: crypto.randomUUID(),
          serviceName: name,
          serviceId: null,
          qty: Number(r.quantity ?? 1),
          unitPrice,
          discount: 0,
          discountType: 'fixed',
          taxRate: 0,
          remarks: 'Added from Used Supplies',
          addToTreatment: defaultAddToTreatment,
          inventoryItemId: r.inventory_item_id,
          freeTextName: null,
          descriptionSnapshot: name,
          skuSnapshot: r.item_code_snapshot,
          unitSnapshot: r.unit_snapshot,
          unitPriceSnapshot: r.unit_price_snapshot,
          taxInclusiveSnapshot: null,
          openPricingSnapshot: null,
          sourceType: 'used_supply',
          sourceId: r.id,
          _isNew: true,
        };
      });
      setLocalItems((prev) => [...prev, ...additions]);
    },
    [defaultAddToTreatment],
  );

  // ── Flush all local items to DB (called by Save button) ──
  const flushItemsToDb = useCallback(async () => {
    if (!db) return;
    const now = new Date().toISOString();

    for (const item of localItems) {
      // PRD.190 — derive free_text_name when no inventory item is bound and
      // no appointment service is selected. source_type defaults to
      // 'inventory_item' if bound, otherwise 'manual' (preserved if already
      // set by an upstream workflow).
      const freeTextName = item.inventoryItemId
        ? null
        : item.serviceId
          ? null
          : item.serviceName.trim() || null;
      const sourceType = item.sourceType ?? (item.inventoryItemId ? 'inventory_item' : 'manual');
      const taxInclusiveSnapshot =
        item.taxInclusiveSnapshot == null ? null : item.taxInclusiveSnapshot ? 1 : 0;
      const openPricingSnapshot =
        item.openPricingSnapshot == null ? null : item.openPricingSnapshot ? 1 : 0;

      if (item._isNew) {
        await db.execute(
          `INSERT INTO invoice_items
            (id, org_id, invoice_id, service_name, service_id, qty, unit_price, discount, discount_type, tax_rate,
             inventory_item_id, free_text_name,
             description_snapshot, sku_snapshot, unit_snapshot, unit_price_snapshot,
             tax_inclusive_snapshot, open_pricing_snapshot, source_type, source_id,
             remarks, add_to_treatment, sort_order, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?,
                   ?, ?,
                   ?, ?, ?, ?,
                   ?, ?, ?, ?,
                   ?, ?, ?, ?, ?)`,
          [
            item.id,
            orgId,
            invoice.id,
            item.serviceName,
            item.serviceId,
            item.qty,
            item.unitPrice,
            item.discount,
            item.discountType,
            item.taxRate,
            item.inventoryItemId,
            freeTextName,
            item.descriptionSnapshot,
            item.skuSnapshot,
            item.unitSnapshot,
            item.unitPriceSnapshot,
            taxInclusiveSnapshot,
            openPricingSnapshot,
            sourceType,
            item.sourceId,
            item.remarks || null,
            item.addToTreatment ? 1 : 0,
            localItems.indexOf(item),
            now,
            now,
          ],
        );
        // PRD.264 — link + mark the source used supply billed (no stock touch).
        if (item.sourceType === 'used_supply' && item.sourceId) {
          await markUsedSupplyBilled({
            db,
            usedSupplyId: item.sourceId,
            invoiceId: invoice.id,
            invoiceItemId: item.id,
          });
        }
      } else {
        await db.execute(
          `UPDATE invoice_items
             SET service_name = ?, service_id = ?, qty = ?, unit_price = ?, discount = ?, discount_type = ?, tax_rate = ?,
                 inventory_item_id = ?, free_text_name = ?,
                 description_snapshot = ?, sku_snapshot = ?, unit_snapshot = ?, unit_price_snapshot = ?,
                 tax_inclusive_snapshot = ?, open_pricing_snapshot = ?, source_type = ?, source_id = ?,
                 remarks = ?, add_to_treatment = ?, updated_at = ?
           WHERE id = ?`,
          [
            item.serviceName,
            item.serviceId,
            item.qty,
            item.unitPrice,
            item.discount,
            item.discountType,
            item.taxRate,
            item.inventoryItemId,
            freeTextName,
            item.descriptionSnapshot,
            item.skuSnapshot,
            item.unitSnapshot,
            item.unitPriceSnapshot,
            taxInclusiveSnapshot,
            openPricingSnapshot,
            sourceType,
            item.sourceId,
            item.remarks || null,
            item.addToTreatment ? 1 : 0,
            now,
            item.id,
          ],
        );
      }
    }

    // Delete items the user removed locally, plus any treatment record
    // auto-created from them (mirrors the invoice-delete cascade so the two
    // stay in sync). `summary` already excludes these, so totals below are right.
    for (const removedId of removedItemIds) {
      await db.execute(`DELETE FROM patient_treatment_records WHERE invoice_item_id = ?`, [
        removedId,
      ]);
      await db.execute(`DELETE FROM invoice_items WHERE id = ?`, [removedId]);
    }

    // Update invoice totals
    await db.execute(
      `UPDATE invoices SET subtotal = ?, discount_total = ?, tax_total = ?, grand_total = ?, updated_at = ? WHERE id = ?`,
      [
        summary.subtotal,
        summary.discountTotal,
        summary.taxTotal,
        summary.grandTotal,
        now,
        invoice.id,
      ],
    );

    // Auto-issue: a draft that now carries a balance is a real bill, not a scratch
    // draft. Forward-only — never revert issued back to draft.
    if (invoice.status === 'draft' && summary.grandTotal > 0) {
      await db.execute(`UPDATE invoices SET status = ?, updated_at = ? WHERE id = ?`, [
        'issued',
        now,
        invoice.id,
      ]);
    }

    // PRD.261 — Create/refresh patient package ownership for any package lines
    // on this invoice. Idempotent by source_invoice_item_id, so running it on
    // every save (and offline replays) never duplicates ownership. Package
    // lines with no included items are skipped with a user-facing reason.
    try {
      const pkgResult = await createPatientPackagesFromInvoice({
        db,
        invoiceId: invoice.id,
        userId,
      });
      setPackageSaleNotice(pkgResult.skipped.map((s) => s.reason));
    } catch (err) {
      console.error('[InvoicesTab] patient package creation failed', err);
    }

    // Clear _isNew flags and the pending-removal list now that they're flushed.
    setLocalItems((prev) => prev.map((item) => ({ ...item, _isNew: undefined })));
    setRemovedItemIds([]);
  }, [db, orgId, invoice.id, invoice.status, localItems, summary, removedItemIds, userId]);

  // ── PRD.238 — Print Invoice payload (line items + totals + meta) ──────────
  // Built from the same `localItems` + `summary` the editor uses so the print
  // matches what the user sees on screen.
  const printItems: InvoicePrintLineItem[] = useMemo(
    () =>
      localItems
        .filter((it) => (it.serviceName ?? '').trim() || (it.unitPrice ?? 0) > 0)
        .map((it) => ({
          name: it.serviceName ?? '',
          description: it.remarks ?? '',
          qty: it.qty ?? 1,
          unitPrice: it.unitPrice ?? 0,
          discount: rowDiscountApplied(it),
          taxRate: it.taxRate ?? 0,
          amount: rowTotal(it),
        })),
    [localItems],
  );

  const printPayments: InvoicePrintPayment[] = useMemo(
    () =>
      invoicePaymentRows.map((p) => ({
        amount: (p.amount_cents ?? 0) / 100,
        method: p.method ?? '',
        paidAtIso: p.paid_at ?? p.created_at ?? '',
      })),
    [invoicePaymentRows],
  );

  const printMeta = useMemo(
    () => ({
      invoiceNumber: invoice.invoice_no ?? '',
      invoiceDateIso: invoice.date_iso ?? invoice.created_at ?? new Date().toISOString(),
      visitDateIso: invoiceEncounterRows[0]?.started_at ?? undefined,
      status:
        invoice.status === 'paid'
          ? 'Paid'
          : invoice.status === 'void'
            ? 'Void'
            : invoice.status === 'draft'
              ? 'Draft'
              : 'Issued',
      providerName: printProviderName,
    }),
    [invoice, invoiceEncounterRows, printProviderName],
  );

  const printTotals = useMemo(
    () => ({
      subtotal: summary.subtotal,
      discountTotal: summary.discountTotal,
      taxTotal: summary.taxTotal,
      grandTotal: summary.grandTotal,
      paymentsTotal: invoicePaymentsTotal,
      balanceDue: Math.max(0, summary.grandTotal - invoicePaymentsTotal),
    }),
    [summary, invoicePaymentsTotal],
  );

  const printDisabled = printItems.length === 0;
  const printAccent = brandTokens['--color-primary'] || primary;

  const handlePrintInvoice = useCallback(async () => {
    // Flush any unsaved item edits before printing so the print mirrors
    // what's actually saved on the invoice.
    await flushItemsToDb();
    triggerPrint('invoice');
  }, [flushItemsToDb]);

  // PRD.239 — WhatsApp share state. Reuses the same `printItems`/`printTotals`
  // payload as Print, so both channels render structurally identical output.
  const [shareInvoiceOpen, setShareInvoiceOpen] = useState(false);
  const invoicePatientWhatsAppNumber = useMemo(() => {
    const c = invoicePatientContactRows[0];
    if (!c?.mobile_number) return null;
    const cc = c.mobile_country_code ? `+${String(c.mobile_country_code).replace(/^\+/, '')} ` : '';
    return cc + c.mobile_number;
  }, [invoicePatientContactRows]);

  const handleShareInvoice = useCallback(async () => {
    // Mirror Print's behavior — flush unsaved edits so the rendered image
    // matches the saved invoice state.
    await flushItemsToDb();
    setShareInvoiceOpen(true);
  }, [flushItemsToDb]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'auto' }}>
      {/* PRD.261 — package lines skipped during ownership creation on save. */}
      {packageSaleNotice.length > 0 && (
        <div
          role="alert"
          style={{
            margin: 'var(--sp-3) var(--sp-5) 0',
            background: '#fef3c7',
            color: '#92400e',
            border: '1px solid #fde68a',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-3) var(--sp-4)',
            fontSize: 'var(--font-size-sm)',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {packageSaleNotice.map((msg, i) => (
            <span key={i}>{msg}</span>
          ))}
        </div>
      )}
      {/* Header */}
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-5)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <span
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Invoice {invoice.invoice_no}
          </span>
          <StatusPill status={(invoice.status as InvoiceStatus) ?? 'draft'} />
          {/* PRD.229 §5.6 / §5.7 / §7.5.1 / AC #10-#12 — Quick Pay
              shortcut placed beside the status pill (per PRD's
              "near invoice status" placement guidance). Clicking it
              opens RecordPaymentDialog invoice-locked, which already
              defaults Method=Cash, amount=remaining balance, date=today,
              and writes a real invoice_payments row (status flips from
              the payment, not a status-only toggle). The explicit
              "+ Add Payment" inside the Payments section remains for
              the manual entry workflow. */}
          {invoice.status !== 'paid' && (
            <button
              type="button"
              onClick={onRecordPayment}
              title="Record a payment for the remaining balance"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                height: 28,
                padding: '0 var(--sp-3)',
                borderRadius: 'var(--radius-full)',
                border: '1px solid #16a34a',
                background: 'none',
                color: '#16a34a',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: 'pointer',
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
              Quick Pay
            </button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          {/* PRD.238 — Print Invoice. Uses the saved org-default invoice
              print layout; falls back to app_config branding when the wizard
              hasn't been run yet. Disabled when there's nothing to print. */}
          <button
            type="button"
            onClick={handlePrintInvoice}
            disabled={printDisabled}
            title={
              printDisabled
                ? 'Add at least one line item before printing'
                : 'Print using the saved invoice layout'
            }
            style={{
              height: 36,
              padding: '0 14px',
              borderRadius: 'var(--radius-md)',
              border: `1px solid ${printDisabled ? 'var(--border)' : primary}`,
              background: 'var(--bg-surface)',
              color: printDisabled ? 'var(--text-disabled)' : primary,
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: printDisabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <NavIcon name="file-text" />
            Print
          </button>
          {/* PRD.239 — Send Invoice to WhatsApp. Renders the same saved
              InvoicePrintLayout to a PNG and ships it through the existing
              WhatsApp infrastructure. Disabled until items + a WhatsApp
              contact + the share endpoint are all available — the modal
              still opens to surface the precise reason via its inline copy. */}
          <button
            type="button"
            onClick={handleShareInvoice}
            disabled={printDisabled}
            title={
              printDisabled
                ? 'Add at least one line item before sharing'
                : 'Send the invoice to the patient on WhatsApp'
            }
            style={{
              height: 36,
              padding: '0 14px',
              borderRadius: 'var(--radius-md)',
              border: `1px solid ${printDisabled ? 'var(--border)' : '#16a34a'}`,
              background: 'var(--bg-surface)',
              color: printDisabled ? 'var(--text-disabled)' : '#16a34a',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: printDisabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <NavIcon name="message-square" />
            WhatsApp
          </button>
          <Button
            variant="gradient"
            size="md"
            onClick={async () => {
              await flushItemsToDb();
              if (localNotes !== (invoice.notes ?? '')) {
                onUpdateNotes(localNotes);
              }
              onSave();
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <NavIcon name="save" />
              Save
            </span>
          </Button>
          <button
            type="button"
            style={{
              width: 34,
              height: 34,
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <NavIcon name="dots-vertical" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            title="Delete Invoice"
            style={{
              width: 34,
              height: 34,
              borderRadius: 'var(--radius-md)',
              border: '1px solid #fecaca',
              background: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#dc2626',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <NavIcon name="trash" />
          </button>
        </div>
      </div>

      {/* PRD.229 §6 / §7 — Inline vertical flow per AC #6 / #7: invoice
          editor first, then Payments section (AC #8 add action via
          PaymentHistoryList's own Record Payment button), then
          Adjustments section (AC #9 add action via InvoiceAdjustmentsList's
          own Add Adjustment button). Replaces the earlier DetailTabStrip
          so payment + adjustment workflows aren't buried behind inner
          tabs when the invoice context is already known. */}
      {
        <>
          {/* Meta */}
          <div
            style={{
              padding: 'var(--sp-3) var(--sp-5)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              display: 'flex',
              gap: 'var(--sp-4)',
            }}
          >
            <span>
              Patient: <strong style={{ color: 'var(--text)' }}>{invoice.patient_name}</strong>
            </span>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Date:
              <input
                type="date"
                value={invoice.date_iso ?? ''}
                onChange={(e) => e.target.value && onUpdateDate(e.target.value)}
                title="Invoice date"
                style={{
                  height: 28,
                  padding: '0 8px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-input)',
                  background: 'var(--input-bg)',
                  color: 'var(--text)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
              />
            </label>
          </div>

          {/* Items */}
          <div style={{ padding: 'var(--sp-4) var(--sp-5)' }}>
            <h3
              style={{
                margin: '0 0 var(--sp-3)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Items
            </h3>
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                overflow: 'visible',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['SERVICE', 'REMARKS', 'QTY', 'UNIT PRICE', 'DISC.', 'TAX %', 'TOTAL', ''].map(
                      (col) => (
                        <th key={col} style={thStyle}>
                          {col}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {uiItems.map((item) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={tdStyle}>
                        <InvoiceLineItemSearch
                          value={item.serviceName}
                          inventoryItemId={item.inventoryItemId}
                          serviceId={item.serviceId}
                          services={activeServices}
                          onTypeFreeText={(name) => {
                            // Free-text typing: if it diverges from the bound
                            // inventory snapshot or service, unbind so the line
                            // doesn't silently misrepresent what was selected.
                            const patch: Parameters<typeof handleUpdateItem>[1] = {
                              serviceName: name,
                            };
                            if (item.inventoryItemId && name !== item.descriptionSnapshot) {
                              patch.inventoryItemId = null;
                              patch.freeTextName = name || null;
                              patch.descriptionSnapshot = null;
                              patch.skuSnapshot = null;
                              patch.unitSnapshot = null;
                              patch.unitPriceSnapshot = null;
                              patch.taxInclusiveSnapshot = null;
                              patch.openPricingSnapshot = null;
                              patch.sourceType = 'manual';
                            } else if (item.serviceId && name !== item.serviceName) {
                              patch.serviceId = null;
                              patch.freeTextName = name || null;
                              patch.sourceType = 'manual';
                            } else if (!item.inventoryItemId && !item.serviceId) {
                              patch.freeTextName = name || null;
                              patch.sourceType = 'manual';
                            }
                            handleUpdateItem(item.id, patch);
                          }}
                          onSelectService={(name, priceCents, svcId) => {
                            const patch: Parameters<typeof handleUpdateItem>[1] = {
                              serviceName: name,
                              serviceId: svcId,
                              // Picking a service clears any inventory binding.
                              inventoryItemId: null,
                              freeTextName: null,
                              descriptionSnapshot: null,
                              skuSnapshot: null,
                              unitSnapshot: null,
                              unitPriceSnapshot: null,
                              taxInclusiveSnapshot: null,
                              openPricingSnapshot: null,
                              sourceType: 'manual',
                            };
                            if (item.unitPrice === 0 && priceCents != null && priceCents > 0) {
                              patch.unitPrice = priceCents / 100;
                            }
                            handleUpdateItem(item.id, patch);
                          }}
                          onSelectInventory={(selection) => {
                            // Same patch shape as PRD.190: bind inventory item,
                            // populate snapshots, prefill display + price. NO
                            // stock movement is touched here — Pharmacy Confirm
                            // Dispense remains the stock-out event.
                            const patch: Parameters<typeof handleUpdateItem>[1] = {
                              serviceName: selection.itemName,
                              serviceId: null,
                              inventoryItemId: selection.inventoryItemId,
                              freeTextName: null,
                              descriptionSnapshot: selection.itemName,
                              skuSnapshot: selection.sku,
                              unitSnapshot: selection.inventoryUnit,
                              unitPriceSnapshot: selection.sellingPrice,
                              taxInclusiveSnapshot: selection.taxInclusive,
                              openPricingSnapshot: selection.openPricing,
                              sourceType: 'inventory_item',
                            };
                            if (selection.sellingPrice != null && item.unitPrice === 0) {
                              patch.unitPrice = selection.sellingPrice;
                            }
                            handleUpdateItem(item.id, patch);
                          }}
                          onClearInventory={() => {
                            handleUpdateItem(item.id, {
                              inventoryItemId: null,
                              freeTextName: item.serviceName.trim() || null,
                              descriptionSnapshot: null,
                              skuSnapshot: null,
                              unitSnapshot: null,
                              unitPriceSnapshot: null,
                              taxInclusiveSnapshot: null,
                              openPricingSnapshot: null,
                              sourceType: 'manual',
                            });
                          }}
                          onClearService={() => {
                            handleUpdateItem(item.id, {
                              serviceId: null,
                              freeTextName: item.serviceName.trim() || null,
                              sourceType: 'manual',
                            });
                          }}
                        />
                        {!item.inventoryItemId && !item.serviceId && !item.serviceName.trim() && (
                          <div
                            style={{
                              marginTop: 4,
                              fontSize: 10,
                              color: '#dc2626',
                            }}
                          >
                            Select an inventory item or enter a line item description.
                          </div>
                        )}
                        {/* PRD.196 / 206 / 234 — Per-line status badge.
                        Medicine lines show Pharmacy dispense status.
                        Product lines (PRD.205+) show Product Release status
                        + a Release Product action when eligible. PRD.234:
                        Products flagged `dispense_via_pharmacy` follow the
                        Medicine path (Pharmacy dispense), not Product
                        Release, even though their item_type is 'product'.
                        Newly-added lines that haven't been saved yet are
                        skipped because there is no DB row to
                        dispense/release against. */}
                        {item.inventoryItemId &&
                          !item._isNew &&
                          (() => {
                            const releaseInfo = releaseStatusByItem.get(item.id);
                            // PRD.266 — only lines that route to an invoice-driven
                            // stock-out (medicine → Pharmacy, product → Release,
                            // vaccination → Pharmacy) show a fulfillment badge.
                            // Injection (non-stock) and supplies (deducted via Used
                            // Supplies) never do. Unknown/unloaded type shows none.
                            if (!routesToInvoiceFulfillment(releaseInfo?.itemType ?? null)) {
                              return null;
                            }
                            const isProductRelease =
                              releaseInfo?.itemType === 'product' &&
                              !releaseInfo?.dispenseViaPharmacy;
                            if (isProductRelease) {
                              const released = releaseInfo?.totalReleased ?? 0;
                              return (
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 4,
                                    marginTop: 4,
                                  }}
                                >
                                  <ReleaseStatusBadge
                                    invoicePaid={isInvoicePaid}
                                    qty={item.qty}
                                    released={released}
                                  />
                                  {isInvoicePaid && released < item.qty && (
                                    <button
                                      type="button"
                                      onClick={() => setReleaseModalLineId(item.id)}
                                      style={{
                                        height: 24,
                                        padding: '0 8px',
                                        borderRadius: 'var(--radius-sm)',
                                        border: '1px solid var(--color-primary)',
                                        background: 'var(--bg-surface)',
                                        color: 'var(--color-primary)',
                                        fontSize: 10,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                      }}
                                    >
                                      Release Product
                                    </button>
                                  )}
                                  {releaseError?.id === item.id && (
                                    <span style={{ fontSize: 10, color: '#991b1b' }}>
                                      {releaseError.text}
                                    </span>
                                  )}
                                </div>
                              );
                            }
                            return (
                              <DispenseStatusBadge
                                invoicePaid={isInvoicePaid}
                                qty={item.qty}
                                dispensed={dispensedByItem.get(item.id) ?? 0}
                              />
                            );
                          })()}
                      </td>
                      <td style={tdStyle}>
                        <input
                          type="text"
                          value={item.remarks}
                          onChange={(e) => handleUpdateItem(item.id, { remarks: e.target.value })}
                          placeholder="e.g. Part #14"
                          style={{ ...cellInputStyle, width: 110 }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <input
                          type="number"
                          value={item.qty}
                          min={1}
                          onChange={(e) =>
                            handleUpdateItem(item.id, {
                              qty: Math.max(1, parseInt(e.target.value) || 1),
                            })
                          }
                          style={{ ...cellInputStyle, width: 50, textAlign: 'center' }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <span
                            style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}
                          >
                            {currencySymbol}
                          </span>
                          <input
                            type="number"
                            value={item.unitPrice}
                            min={0}
                            onChange={(e) =>
                              handleUpdateItem(item.id, {
                                unitPrice: Math.max(0, parseFloat(e.target.value) || 0),
                              })
                            }
                            style={{ ...cellInputStyle, width: 70 }}
                          />
                        </div>
                      </td>
                      <td style={tdStyle}>
                        {/* PRD.228 — Discount with type toggle. The leading
                            chip is the type selector (₱ fixed, % percentage)
                            and the number input keeps its raw typed value
                            across switches per the chosen UX. Effective
                            discount math is clamped in consultation-data.ts
                            so out-of-range values can't break totals. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <button
                            type="button"
                            onClick={() =>
                              handleUpdateItem(item.id, {
                                discountType:
                                  item.discountType === 'percentage' ? 'fixed' : 'percentage',
                              })
                            }
                            title={
                              item.discountType === 'percentage'
                                ? `Percentage discount (click to switch to fixed ${currencySymbol})`
                                : `Fixed ${currencySymbol} discount (click to switch to %)`
                            }
                            style={discountTypeBtnStyle(item.discountType)}
                          >
                            {item.discountType === 'percentage' ? '%' : currencySymbol}
                          </button>
                          <input
                            type="number"
                            value={item.discount}
                            min={0}
                            max={item.discountType === 'percentage' ? 100 : undefined}
                            onChange={(e) =>
                              handleUpdateItem(item.id, {
                                discount: Math.max(0, parseFloat(e.target.value) || 0),
                              })
                            }
                            style={{ ...cellInputStyle, width: 60 }}
                          />
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <input
                          type="number"
                          value={item.taxRate}
                          min={0}
                          max={100}
                          onChange={(e) =>
                            handleUpdateItem(item.id, {
                              taxRate: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)),
                            })
                          }
                          style={{ ...cellInputStyle, width: 50, textAlign: 'center' }}
                        />
                      </td>
                      <td
                        style={{
                          ...tdStyle,
                          fontWeight: 'var(--font-weight-medium)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fmt(rowTotal(item))}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 'var(--sp-2)',
                          }}
                        >
                          <label
                            title="Add to Treatment Record"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              cursor: 'pointer',
                              fontSize: 'var(--font-size-xs)',
                              color: item.addToTreatment
                                ? 'var(--color-primary)'
                                : 'var(--text-placeholder)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={item.addToTreatment}
                              onChange={(e) =>
                                handleUpdateItem(item.id, { addToTreatment: e.target.checked })
                              }
                              style={{ accentColor: 'var(--color-primary)' }}
                            />
                            Tx
                          </label>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(item.id)}
                            title="Remove item"
                            aria-label="Remove item"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 26,
                              height: 26,
                              borderRadius: 'var(--radius-sm)',
                              border: 'none',
                              background: 'transparent',
                              color: 'var(--text-placeholder)',
                              cursor: 'pointer',
                              padding: 0,
                              flexShrink: 0,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'var(--color-danger-bg, #fef2f2)';
                              e.currentTarget.style.color = 'var(--color-danger-text, #dc2626)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                              e.currentTarget.style.color = 'var(--text-placeholder)';
                            }}
                          >
                            <NavIcon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              style={{
                marginTop: 'var(--sp-2)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                onClick={handleAddItem}
                style={{
                  border: 'none',
                  background: 'none',
                  color: primary,
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  cursor: 'pointer',
                  padding: 'var(--sp-1) 0',
                }}
              >
                + Add item
              </button>

              {/* PRD.264 — pull ready-for-billing Used Supplies into the invoice. */}
              <AddFromUsedSuppliesButton
                patientId={invoice.patient_id ?? ''}
                encounterId={invoice.encounter_id ?? ''}
                onAdd={handleAddUsedSupplyItems}
                primary={primary}
              />

              {/* PRD.209 — Add Service from Appointment.
              Visible only when this invoice's encounter is linked to an
              appointment with a selected service. When the appointment date
              doesn't match the consultation date we render the button in a
              disabled state with helper text instead of hiding it, so users
              understand why the action isn't available. Duplicate detection
              checks the current invoice's local items (including unsaved
              additions) for an existing line with the same service_id. */}
              {linkedAppointmentService &&
                (() => {
                  const alreadyAdded = localItems.some(
                    (it) => it.serviceId === linkedAppointment?.service_id,
                  );
                  const disabled = !datesMatch || alreadyAdded;
                  const helper = !datesMatch
                    ? 'Appointment service is from a different date.'
                    : alreadyAdded
                      ? 'Service already added.'
                      : null;
                  const priceLabel =
                    linkedAppointmentService.price_cents != null
                      ? fmt(linkedAppointmentService.price_cents / 100)
                      : null;
                  return (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-2)',
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleAddAppointmentService}
                        disabled={disabled}
                        title={
                          helper ?? 'Adds the service selected when this appointment was booked.'
                        }
                        style={{
                          height: 30,
                          padding: '0 var(--sp-3)',
                          borderRadius: 'var(--radius-sm)',
                          border: `1px solid ${disabled ? 'var(--border)' : primary}`,
                          background: 'var(--bg-surface)',
                          color: disabled ? 'var(--text-muted)' : primary,
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 'var(--font-weight-semi)',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        + Add Service from Appointment
                        {linkedAppointmentService.name && !alreadyAdded
                          ? ` (${linkedAppointmentService.name}${priceLabel ? ` · ${priceLabel}` : ''})`
                          : ''}
                      </button>
                      {helper && (
                        <span
                          style={{
                            fontSize: 10,
                            color: alreadyAdded ? 'var(--text-muted)' : '#b45309',
                          }}
                        >
                          {helper}
                        </span>
                      )}
                    </div>
                  );
                })()}

              {/* PRD.210 — Add Items from Prescription.
              Always visible so users discover it; disabled with helper
              text when there are no inventory-bound prescription items
              for this visit, or when every eligible item has already
              been added to this invoice (duplicate-keyed by
              source_type='prescription' + source_id=prescription_id,
              with an inventory_item_id fallback for legacy lines that
              predate the source_* columns). */}
              {(() => {
                const addedPrescriptionIds = new Set(
                  localItems
                    .filter((it) => it.sourceType === 'prescription' && it.sourceId)
                    .map((it) => it.sourceId as string),
                );
                const addedInventoryIds = new Set(
                  localItems
                    .filter((it) => it.inventoryItemId)
                    .map((it) => it.inventoryItemId as string),
                );
                const eligibleCandidates = prescriptionCandidateRows.filter((r) => {
                  if (addedPrescriptionIds.has(r.prescription_id)) return false;
                  if (
                    !addedPrescriptionIds.size &&
                    r.inventory_item_id &&
                    addedInventoryIds.has(r.inventory_item_id)
                  ) {
                    return false;
                  }
                  return true;
                });
                const totalCandidates = prescriptionCandidateRows.length;
                const remaining = eligibleCandidates.length;
                const disabled = remaining === 0;
                const helper =
                  totalCandidates === 0
                    ? 'No prescription items for this visit.'
                    : remaining === 0
                      ? 'All eligible prescription items are already added.'
                      : null;
                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-2)',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setPrescriptionModalOpen(true)}
                      disabled={disabled}
                      title={
                        helper ??
                        'Adds inventory-linked Medicine/Product prescription items to this invoice.'
                      }
                      style={{
                        height: 30,
                        padding: '0 var(--sp-3)',
                        borderRadius: 'var(--radius-sm)',
                        border: `1px solid ${disabled ? 'var(--border)' : primary}`,
                        background: 'var(--bg-surface)',
                        color: disabled ? 'var(--text-muted)' : primary,
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-semi)',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      + Add Items from Prescription
                      {remaining > 0 ? ` (${remaining})` : ''}
                    </button>
                    {helper && (
                      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{helper}</span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Reference No */}
          <div style={{ padding: '0 var(--sp-5) var(--sp-4)' }}>
            <h3
              style={{
                margin: '0 0 var(--sp-2)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Reference No.
            </h3>
            {/* Official / Unofficial toggle */}
            <div
              style={{
                display: 'flex',
                background: 'var(--bg-surface-2)',
                borderRadius: 'var(--radius-md)',
                padding: 2,
                gap: 2,
                marginBottom: 8,
                width: 'fit-content',
              }}
            >
              {(['official', 'unofficial'] as const).map((t) => {
                const active = (invoice.reference_type ?? 'unofficial') === t;
                return (
                  <button
                    key={t}
                    onClick={() => onUpdateReferenceType(t)}
                    style={{
                      height: 28,
                      padding: '0 12px',
                      borderRadius: 'var(--radius-sm)',
                      border: 'none',
                      background: active ? 'var(--bg-surface)' : 'transparent',
                      boxShadow: active ? 'var(--shadow-sm)' : 'none',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: active ? 'var(--text)' : 'var(--text-muted)',
                      cursor: 'pointer',
                      textTransform: 'capitalize',
                    }}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
            <input
              type="text"
              value={invoice.reference_no ?? ''}
              onChange={(e) => onUpdateReferenceNo(e.target.value)}
              placeholder={
                (invoice.reference_type ?? 'unofficial') === 'official'
                  ? 'e.g. OR-2026-001'
                  : 'e.g. UR-001, receipt #'
              }
              style={{
                width: '100%',
                height: 36,
                padding: '0 var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-input)',
                background: 'var(--input-bg)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--input-text)',
                outline: 'none',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Notes */}
          <div style={{ padding: '0 var(--sp-5) var(--sp-4)' }}>
            <h3
              style={{
                margin: '0 0 var(--sp-2)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Notes
            </h3>
            <textarea
              value={localNotes}
              onChange={(e) => setLocalNotes(e.target.value)}
              placeholder="Add billing notes..."
              rows={3}
              style={{
                width: '100%',
                padding: 'var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-input)',
                background: 'var(--input-bg)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--input-text)',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Summary */}
          <div style={{ padding: '0 var(--sp-5) var(--sp-5)' }}>
            <h3
              style={{
                margin: '0 0 var(--sp-3)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Summary
            </h3>
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--sp-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-2)',
              }}
            >
              <SummaryRow label="Subtotal" value={fmt(summary.subtotal)} />
              <SummaryRow label="Discount" value={`-${fmt(summary.discountTotal)}`} />
              <SummaryRow label="Tax" value={fmt(summary.taxTotal)} />
              <div
                style={{
                  borderTop: '1px solid var(--border)',
                  paddingTop: 'var(--sp-2)',
                  marginTop: 'var(--sp-1)',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text)',
                  }}
                >
                  Total
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--text)',
                  }}
                >
                  {fmt(summary.grandTotal)}
                </span>
              </div>
            </div>
          </div>
        </>
      }

      {/* PRD.229 §6.2 / AC #6+#8 — Payments section, always visible below
          the invoice editor with its own "+ Record Payment" action. */}
      <PaymentHistoryList
        invoiceId={invoice.id}
        invoiceGrandTotal={invoice.grand_total}
        onRecordPayment={onRecordPayment}
        onEditPayment={onEditPayment}
      />

      {/* PRD.247 — Billing Item Payment Status: per-item Charge / Paid /
          Outstanding / Status from real allocations (unallocated payments are
          flagged, not guessed). Billing owns the item-level settlement view. */}
      <InvoiceItemPaymentStatus invoiceId={invoice.id} />

      {/* PRD.229 §6.3 / AC #7+#9 — Adjustments section, always visible
          below the Payments section with its own "+ Add Adjustment"
          action (PRD.227 invoice-scoped credit/debit list). */}
      <InvoiceAdjustmentsList invoiceId={invoice.id} onAdd={() => setAdjustmentOpen(true)} />

      {/* Modal lives outside the tab gate so it stays open across switches. */}
      <AddAdjustmentDialog
        open={adjustmentOpen}
        onOpenChange={setAdjustmentOpen}
        patientId={invoice.patient_id ?? ''}
        orgId={orgId}
        prefilledInvoiceId={invoice.id}
      />

      {/* PRD.210 — Add Items from Prescription picker modal. Surfaces
          inventory-bound prescription lines from the current visit so
          the user can pick which to bill. Already-added rows render
          disabled with an "Added" hint. Confirm pushes selections into
          the local invoice items list (no DB write yet — Save still
          flushes). NO stock movement is touched. */}
      {prescriptionModalOpen && (
        <PrescriptionItemsModal
          rows={prescriptionCandidateRows}
          alreadyAddedPrescriptionIds={
            new Set(
              localItems
                .filter((it) => it.sourceType === 'prescription' && it.sourceId)
                .map((it) => it.sourceId as string),
            )
          }
          alreadyAddedInventoryIds={
            new Set(
              localItems
                .filter((it) => it.inventoryItemId)
                .map((it) => it.inventoryItemId as string),
            )
          }
          portalStyle={brandTokens as CSSProperties}
          onClose={() => setPrescriptionModalOpen(false)}
          onConfirm={(selected) => {
            handleAddPrescriptionItems(selected);
            setPrescriptionModalOpen(false);
          }}
        />
      )}

      {/* PRD.206 — Product Release modal. Rendered when the user clicks
          Release Product on an eligible Product invoice line. The modal
          has its own data fetch for available batches; on confirm it
          calls confirmProductRelease which writes product_releases +
          product_release_lines + an outbound stock_movements row. */}
      {releaseModalLineId &&
        (() => {
          const line = localItems.find((it) => it.id === releaseModalLineId);
          const releaseInfo = releaseStatusByItem.get(releaseModalLineId);
          if (!line || !line.inventoryItemId) return null;
          const remaining = Math.max(0, (line.qty ?? 0) - (releaseInfo?.totalReleased ?? 0));
          if (remaining <= 0) return null;
          const itemName =
            line.descriptionSnapshot ?? line.serviceName ?? line.freeTextName ?? 'Product';
          return (
            <ProductReleaseModal
              inventoryItemId={line.inventoryItemId}
              invoiceItemId={line.id}
              itemName={itemName}
              sku={line.skuSnapshot}
              unit={line.unitSnapshot}
              qtyToRelease={remaining}
              saving={releaseSavingItemId === line.id}
              portalStyle={brandTokens as CSSProperties}
              onClose={() => {
                setReleaseModalLineId(null);
                setReleaseError(null);
              }}
              onConfirm={(args) => {
                void confirmProductRelease(args);
              }}
            />
          );
        })()}

      {/* PRD.238 — Hidden print-only mount. Rendered as a sibling of the
          editor; the scoped @media print stylesheet hides the app shell and
          shows only this when `triggerPrint('invoice')` fires. */}
      <style>{INVOICE_PRINT_STYLES}</style>
      {!printDisabled && (
        <div className="ip-print-only" aria-hidden>
          <InvoicePrintLayout
            settings={printSettings}
            patient={printPatient}
            meta={printMeta}
            items={printItems}
            payments={printPayments}
            totals={printTotals}
            formatMoney={fmt}
            accentColor={printAccent}
          />
        </div>
      )}

      {/* PRD.239 — WhatsApp share modal. Reuses the same InvoicePrintLayout
          source (no separate WhatsApp-only invoice layout exists). The modal
          renders the layout offscreen, converts it to a PNG, and sends via
          the existing WhatsApp infrastructure. */}
      <ShareInvoiceModal
        open={shareInvoiceOpen}
        onOpenChange={setShareInvoiceOpen}
        patientId={invoice.patient_id ?? ''}
        patientName={printPatient.name}
        patientWhatsAppNumber={invoicePatientWhatsAppNumber}
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoice_no ?? ''}
        encounterId={invoice.encounter_id ?? undefined}
        printSettings={printSettings}
        printPatient={printPatient}
        printMeta={printMeta}
        printItems={printItems}
        printPayments={printPayments}
        printTotals={printTotals}
        formatMoney={fmt}
        accentColor={printAccent}
      />
    </div>
  );
}

// ─── Shared sub-components ───

/**
 * PRD.227 §11 — Tab strip used inside the invoice detail drawer.
 * Three clearly distinct sections: Invoice (line items + summary +
 * notes), Payments (invoice_payments history), Adjustments
 * (billing_adjustments history). Mirrors the segmented control style
 * used by EncountersListPage's viewMode toggle so the look is familiar.
 */
function StatusPill({ status }: { status: InvoiceStatus }) {
  const styles: Record<InvoiceStatus, { bg: string; color: string }> = {
    draft: { bg: '#f1f5f9', color: '#64748b' },
    issued: { bg: '#fef3c7', color: '#92400e' },
    paid: { bg: '#d1fae5', color: '#065f46' },
  };
  const s = styles[status];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        color: s.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '20px',
        textTransform: 'capitalize',
      }}
    >
      {status}
    </span>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>{value}</span>
    </div>
  );
}

// ─── Styles ───

/**
 * Status badge tones used inside the singlePane invoice pill tabs.
 * Mirrors the StatusPill colors so the look is consistent.
 */
const INVOICE_PILL_STATUS_STYLES: Record<InvoiceStatus, { bg: string; color: string }> = {
  draft: { bg: '#f1f5f9', color: '#64748b' },
  issued: { bg: '#fef3c7', color: '#92400e' },
  paid: { bg: '#d1fae5', color: '#065f46' },
};

/**
 * PRD.228 — Compact discount-type chip inside the invoice row.
 * Shows `₱` for fixed and `%` for percentage. Clicking toggles the
 * meaning of the adjacent value input. Tone shifts to the primary
 * brand color so the active mode is unambiguous at a glance.
 */
function discountTypeBtnStyle(type: 'fixed' | 'percentage'): CSSProperties {
  const isPct = type === 'percentage';
  return {
    width: 22,
    height: 24,
    borderRadius: 'var(--radius-sm)',
    border: isPct ? '1px solid var(--color-primary)' : '1px solid var(--border)',
    background: isPct ? 'var(--color-primary-bg, #eef2ff)' : 'var(--bg-surface)',
    color: isPct ? 'var(--color-primary, #4f46e5)' : 'var(--text-muted)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    flexShrink: 0,
    lineHeight: 1,
  };
}

const thStyle: CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  textAlign: 'left',
  fontSize: '10px',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderBottom: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  fontSize: 'var(--font-size-sm)',
  verticalAlign: 'middle',
};

const cellInputStyle: CSSProperties = {
  height: 30,
  padding: '0 var(--sp-2)',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  boxSizing: 'border-box',
};

const selectStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--sp-8) 0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  cursor: 'pointer',
  appearance: 'none',
  width: '100%',
  boxSizing: 'border-box',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
};

// ─── PRD.196 — Dispense Status Badge ────────────────────────────────
//
// Read-only indicator on Medicine invoice lines. The badge is purely
// derived from existing data (invoice.status + confirmed
// pharmacy_dispense_lines) — no schema changes, no writes. Stock is NOT
// touched by anything in this component; Pharmacy Confirm Dispense
// remains the only stock-out event for invoice Medicine lines.
//
// Status mapping:
//   invoice not paid               → 'Awaiting Payment'  (gray)
//   invoice paid, 0 dispensed      → 'Ready for Pharmacy' (blue)
//   0 < dispensed < qty            → 'Partially Dispensed (X/Y)' (amber)
//   dispensed >= qty               → 'Dispensed' (green)

function DispenseStatusBadge({
  invoicePaid,
  qty,
  dispensed,
}: {
  invoicePaid: boolean;
  qty: number;
  dispensed: number;
}) {
  let label: string;
  let bg: string;
  let color: string;
  let border: string;

  if (dispensed >= qty && qty > 0) {
    label = 'Dispensed';
    bg = '#f0fdf4';
    color = '#16a34a';
    border = '#bbf7d0';
  } else if (dispensed > 0) {
    label = `Partially Dispensed (${dispensed}/${qty})`;
    bg = '#fffbeb';
    color = '#b45309';
    border = '#fde68a';
  } else if (invoicePaid) {
    label = 'Ready for Pharmacy';
    bg = '#eff6ff';
    color = '#1d4ed8';
    border = '#bfdbfe';
  } else {
    label = 'Awaiting Payment';
    bg = 'var(--bg-surface-2, #f3f4f6)';
    color = 'var(--text-muted, #6b7280)';
    border = 'var(--border, rgba(15,23,42,0.12))';
  }

  return (
    <span
      title="Stock is deducted only after Pharmacy confirms the dispense."
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        marginTop: 4,
        height: 18,
        padding: '0 6px',
        borderRadius: 'var(--radius-full, 9999px)',
        background: bg,
        color,
        border: `1px solid ${border}`,
        fontSize: 10,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

// ─── PRD.206 — Product Release Status Badge ─────────────────────────
//
// Mirror of DispenseStatusBadge for Product invoice lines. Read-only;
// derived from invoice.status + confirmed product_release_lines.
//
// Status mapping:
//   invoice not paid               → 'Awaiting Payment'  (gray)
//   invoice paid, 0 released       → 'Ready for Release' (indigo)
//   0 < released < qty             → 'Partially Released (X/Y)' (amber)
//   released >= qty                → 'Released' (green)

function ReleaseStatusBadge({
  invoicePaid,
  qty,
  released,
}: {
  invoicePaid: boolean;
  qty: number;
  released: number;
}) {
  let label: string;
  let bg: string;
  let color: string;
  let border: string;

  if (released >= qty && qty > 0) {
    label = 'Released';
    bg = '#f0fdf4';
    color = '#16a34a';
    border = '#bbf7d0';
  } else if (released > 0) {
    label = `Partially Released (${released}/${qty})`;
    bg = '#fffbeb';
    color = '#b45309';
    border = '#fde68a';
  } else if (invoicePaid) {
    label = 'Ready for Release';
    bg = '#eef2ff';
    color = '#4338ca';
    border = '#c7d2fe';
  } else {
    label = 'Awaiting Payment';
    bg = 'var(--bg-surface-2, #f3f4f6)';
    color = 'var(--text-muted, #6b7280)';
    border = 'var(--border, rgba(15,23,42,0.12))';
  }

  return (
    <span
      title="Stock is deducted only when Product Release is confirmed."
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 18,
        padding: '0 6px',
        borderRadius: 'var(--radius-full, 9999px)',
        background: bg,
        color,
        border: `1px solid ${border}`,
        fontSize: 10,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

// ─── PRD.210 — Add Items from Prescription Modal ───────────────────
//
// Picker for inventory-bound prescription lines from the current
// visit. The parent feeds in the candidate rows + the set of
// prescription/inventory ids already on the invoice; rows already
// added render disabled with an "Added" hint. On confirm the parent
// pushes the selected rows into local invoice items state — no DB
// write happens here. NO stock movement is touched.

function PrescriptionItemsModal({
  rows,
  alreadyAddedPrescriptionIds,
  alreadyAddedInventoryIds,
  portalStyle,
  onClose,
  onConfirm,
}: {
  rows: InvoicePrescriptionCandidateRow[];
  alreadyAddedPrescriptionIds: Set<string>;
  alreadyAddedInventoryIds: Set<string>;
  portalStyle: CSSProperties;
  onClose: () => void;
  onConfirm: (selected: InvoicePrescriptionCandidateRow[]) => void;
}) {
  const fmt = useFormatMoney();
  const isAlreadyAdded = useCallback(
    (r: InvoicePrescriptionCandidateRow) => {
      if (alreadyAddedPrescriptionIds.has(r.prescription_id)) return true;
      if (
        alreadyAddedPrescriptionIds.size === 0 &&
        r.inventory_item_id &&
        alreadyAddedInventoryIds.has(r.inventory_item_id)
      ) {
        return true;
      }
      return false;
    },
    [alreadyAddedPrescriptionIds, alreadyAddedInventoryIds],
  );

  // Default selection: every eligible (not already added) candidate.
  // Saves a click in the common case where the user just wants to bill
  // the whole prescription. Already-added rows stay unchecked + disabled.
  const [checked, setChecked] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const r of rows) {
      if (!isAlreadyAdded(r)) init[r.prescription_id] = true;
    }
    return init;
  });

  const toggle = (id: string) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  const selectedRows = useMemo(
    () => rows.filter((r) => checked[r.prescription_id] && !isAlreadyAdded(r)),
    [rows, checked, isAlreadyAdded],
  );

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Add Items from Prescription"
      maxWidth={640}
      portalStyle={portalStyle}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            size="md"
            disabled={selectedRows.length === 0}
            onClick={() => onConfirm(selectedRows)}
          >
            Add Selected to Invoice
            {selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}
          </Button>
        </div>
      }
    >
      <div style={{ padding: 'var(--sp-3) var(--sp-5) var(--sp-5)' }}>
        <div
          style={{
            fontSize: 10,
            color: 'var(--text-muted)',
            marginBottom: 'var(--sp-3)',
          }}
        >
          Free-text prescription items can be added manually from the line search.
        </div>

        {rows.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-surface-2, #fafafa)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              textAlign: 'center',
            }}
          >
            No prescription items for this visit.
          </div>
        ) : (
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
            }}
          >
            {rows.map((r, idx) => {
              const added = isAlreadyAdded(r);
              const isMedicine = r.inventory_item_type !== 'product';
              const displayName =
                r.item_name_snapshot?.trim() ||
                r.free_text_name?.trim() ||
                r.item_name?.trim() ||
                '—';
              const dosageBits: string[] = [];
              if (r.dose_snapshot?.trim()) {
                dosageBits.push(
                  `${r.dose_snapshot.trim()}${r.dose_unit_snapshot?.trim() ? ` ${r.dose_unit_snapshot.trim()}` : ''}`,
                );
              }
              if (r.frequency_snapshot?.trim()) dosageBits.push(r.frequency_snapshot.trim());
              if (r.duration_snapshot?.trim()) dosageBits.push(r.duration_snapshot.trim());
              const dosageSummary =
                dosageBits.join(' · ') ||
                r.default_instruction_snapshot?.trim() ||
                r.usage_snapshot?.trim() ||
                '';
              const priceLabel =
                r.inventory_selling_price != null ? fmt(r.inventory_selling_price) : '—';
              const isChecked = !added && !!checked[r.prescription_id];
              return (
                <label
                  key={r.prescription_id}
                  htmlFor={`presc-${r.prescription_id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--sp-3)',
                    padding: 'var(--sp-3)',
                    borderTop: idx === 0 ? 'none' : '1px solid var(--border)',
                    background: added ? 'var(--bg-surface-2, #fafafa)' : 'var(--bg-surface)',
                    cursor: added ? 'not-allowed' : 'pointer',
                    opacity: added ? 0.7 : 1,
                  }}
                >
                  <input
                    id={`presc-${r.prescription_id}`}
                    type="checkbox"
                    checked={isChecked}
                    disabled={added}
                    onChange={() => toggle(r.prescription_id)}
                    style={{
                      marginTop: 3,
                      accentColor: 'var(--color-primary)',
                      cursor: added ? 'not-allowed' : 'pointer',
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-2)',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-semi)',
                          color: 'var(--text)',
                        }}
                      >
                        {displayName}
                      </span>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: 18,
                          padding: '0 6px',
                          borderRadius: 'var(--radius-full, 9999px)',
                          background: isMedicine ? '#eff6ff' : '#eef2ff',
                          color: isMedicine ? '#1d4ed8' : '#4338ca',
                          border: `1px solid ${isMedicine ? '#bfdbfe' : '#c7d2fe'}`,
                          fontSize: 10,
                          fontWeight: 600,
                        }}
                      >
                        {isMedicine ? 'Medicine' : 'Product'}
                      </span>
                      {r.sku_snapshot && (
                        <span
                          style={{
                            fontSize: 10,
                            color: 'var(--text-muted)',
                          }}
                        >
                          SKU {r.sku_snapshot}
                        </span>
                      )}
                      {added && (
                        <span
                          style={{
                            fontSize: 10,
                            color: '#16a34a',
                            fontWeight: 600,
                          }}
                        >
                          Already added
                        </span>
                      )}
                    </div>
                    {dosageSummary && (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {dosageSummary}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color:
                        r.inventory_selling_price != null ? 'var(--text)' : 'var(--text-muted)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {priceLabel}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── PRD.206 — Product Release Modal ───────────────────────────────
//
// Opens from the Release Product button on a paid Product invoice line.
// Loads existing batches via qAvailableBatchesForProductRelease — does
// NOT create batches. Confirm calls the parent's confirmProductRelease,
// which writes product_releases + product_release_lines + outbound
// stock_movements.

function ProductReleaseModal({
  inventoryItemId,
  invoiceItemId,
  itemName,
  sku,
  unit,
  qtyToRelease,
  saving,
  portalStyle,
  onClose,
  onConfirm,
}: {
  inventoryItemId: string;
  invoiceItemId: string;
  itemName: string;
  sku: string | null;
  unit: string | null;
  qtyToRelease: number;
  saving: boolean;
  portalStyle: CSSProperties;
  onClose: () => void;
  onConfirm: (args: {
    invoiceItemId: string;
    inventoryItemId: string;
    qtyToRelease: number;
    itemNameSnapshot: string;
    skuSnapshot: string | null;
    unitSnapshot: string | null;
    batch: ProductReleaseBatchOptionRow | null;
  }) => void;
}) {
  const { data: batchRows = [] } = usePowerSyncQuery<ProductReleaseBatchOptionRow>(
    qAvailableBatchesForProductRelease(inventoryItemId),
  );
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const selectedBatch = useMemo(
    () => batchRows.find((b) => b.inventory_batch_id === selectedBatchId) ?? null,
    [batchRows, selectedBatchId],
  );
  const hasAnyBatches = batchRows.length > 0;
  const insufficient =
    selectedBatch != null &&
    selectedBatch.available_quantity != null &&
    qtyToRelease > selectedBatch.available_quantity;

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Release Product"
      maxWidth={560}
      portalStyle={portalStyle}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button variant="outline" size="md" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            size="md"
            disabled={
              saving ||
              insufficient ||
              // If the product has any batches at all, force the user to
              // pick one. Products with zero batches (no lot tracking
              // configured) can be released without a batch reference.
              (hasAnyBatches && !selectedBatch)
            }
            onClick={() =>
              onConfirm({
                invoiceItemId,
                inventoryItemId,
                qtyToRelease,
                itemNameSnapshot: itemName,
                skuSnapshot: sku,
                unitSnapshot: unit,
                batch: selectedBatch,
              })
            }
          >
            {saving ? 'Saving…' : 'Confirm Release'}
          </Button>
        </div>
      }
    >
      <div style={{ padding: 'var(--sp-3) var(--sp-5) var(--sp-5)' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            marginBottom: 'var(--sp-3)',
          }}
        >
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            Releasing{' '}
            <strong style={{ color: 'var(--text)' }}>
              {qtyToRelease} {unit ?? ''}
            </strong>{' '}
            of <strong style={{ color: 'var(--text)' }}>{itemName}</strong>
            {sku ? ` (SKU ${sku})` : ''}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
            Confirm Release deducts stock immediately. New batches are created via Delivery Order
            receiving or Inventory Adjustments — not from here.
          </div>
        </div>

        {!hasAnyBatches ? (
          <div
            style={{
              padding: 'var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-surface-2, #fafafa)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              textAlign: 'center',
            }}
          >
            This product has no tracked batches. The release will be recorded without a batch
            reference. Receive stock via a Delivery Order or create an Inventory Adjustment if you
            want batch/lot tracking.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <ReleaseTh>Batch Number</ReleaseTh>
                <ReleaseTh>Expiry</ReleaseTh>
                <ReleaseTh align="right">Available</ReleaseTh>
                <ReleaseTh />
              </tr>
            </thead>
            <tbody>
              {batchRows.map((b) => {
                const avail = b.available_quantity ?? 0;
                const tooFew = avail < qtyToRelease;
                const isSelected = b.inventory_batch_id === selectedBatchId;
                return (
                  <tr
                    key={b.inventory_batch_id}
                    style={{
                      borderBottom: '1px solid var(--border)',
                      background: isSelected ? 'var(--color-primary-bg, #eef2ff)' : undefined,
                    }}
                  >
                    <td style={{ padding: '8px 6px', fontSize: 'var(--font-size-sm)' }}>
                      {b.batch_number ?? '—'}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {b.expiry_date ?? '—'}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        textAlign: 'right',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: tooFew ? '#991b1b' : 'var(--text)',
                      }}
                    >
                      {avail}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                      <Button
                        variant={isSelected ? 'gradient' : 'outline'}
                        size="md"
                        disabled={avail <= 0}
                        onClick={() => setSelectedBatchId(b.inventory_batch_id)}
                      >
                        {isSelected ? 'Selected' : 'Select'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}

function ReleaseTh({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '10px 8px',
        textAlign: align,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #fafafa)',
      }}
    >
      {children}
    </th>
  );
}

// ─── PRD.197 — Unified Invoice Line Item Search ─────────────────────
//
// One search field that finds Services + Inventory Medicine + Manual
// fallback. Replaces the older split between `ServiceAutocomplete` and
// the separate `InvoiceInventoryPicker` button so users can simply type
// an item name and pick whichever match is correct.
//
// Selection mapping is fully delegated to the parent via three callbacks
// — the parent applies the same patches it applied before, so PRD.190
// snapshot writes and PRD.196 dispense-status badge logic are preserved
// byte-for-byte. NO writes happen inside this component, NO stock
// movements are touched. Pharmacy Confirm Dispense remains the sole
// stock-out event for invoice Medicine lines.

interface InvoiceLineItemSearchProps {
  value: string;
  inventoryItemId: string | null;
  serviceId: string | null;
  services: AppointmentServiceRow[];
  onSelectService: (name: string, priceCents: number | null, serviceId: string) => void;
  onSelectInventory: (selection: InventoryPickerSelection) => void;
  onTypeFreeText: (text: string) => void;
  onClearInventory: () => void;
  onClearService: () => void;
}

function InvoiceLineItemSearch({
  value,
  inventoryItemId,
  serviceId,
  services,
  onSelectService,
  onSelectInventory,
  onTypeFreeText,
  onClearInventory,
  onClearService,
}: InvoiceLineItemSearchProps) {
  const fmt = useFormatMoney();
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState(value);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 200);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // PRD.205 — invoice unified search includes both Medicine and Product
  // inventory items. The picker query now accepts an `itemTypes` filter
  // and we split results into two sections so users can tell them apart.
  const { data: inventoryRows = [] } = usePowerSyncQuery<InventoryPickerCandidateRow>(
    // PRD.261 — packages join the invoice picker so they can be sold as a
    // single line at the package selling price (no child expansion, no stock).
    // PRD.264 — supplies join too, but only sellable items appear (is_sellable),
    // so non-sellable expendables are hidden from billing search.
    // PRD.266 — vaccination + injection are sellable and appear in Billing.
    qInventoryPickerCandidates(
      debounced,
      ['medicine', 'product', 'package', 'supply', 'vaccination', 'injection'],
      true,
    ),
  );
  const medicineInventoryRows = useMemo(
    () => inventoryRows.filter((r) => r.item_type === 'medicine'),
    [inventoryRows],
  );
  const vaccinationInventoryRows = useMemo(
    () => inventoryRows.filter((r) => r.item_type === 'vaccination'),
    [inventoryRows],
  );
  const injectionInventoryRows = useMemo(
    () => inventoryRows.filter((r) => r.item_type === 'injection'),
    [inventoryRows],
  );
  const productInventoryRows = useMemo(
    () => inventoryRows.filter((r) => r.item_type === 'product'),
    [inventoryRows],
  );
  const packageInventoryRows = useMemo(
    () => inventoryRows.filter((r) => r.item_type === 'package'),
    [inventoryRows],
  );
  const supplyInventoryRows = useMemo(
    () => inventoryRows.filter((r) => r.item_type === 'supply'),
    [inventoryRows],
  );

  // Services match locally against the already-loaded list.
  const serviceMatches = useMemo(() => {
    if (!value.trim()) return services.slice(0, 6);
    const q = value.toLowerCase();
    return services.filter((s) => (s.name ?? '').toLowerCase().includes(q)).slice(0, 8);
  }, [value, services]);

  const showManualOption =
    value.trim().length > 0 &&
    !serviceMatches.some((s) => (s.name ?? '').toLowerCase() === value.toLowerCase()) &&
    !inventoryRows.some((r) => (r.item_name ?? '').toLowerCase() === value.toLowerCase());

  const totalResults = inventoryRows.length + serviceMatches.length + (showManualOption ? 1 : 0);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', flex: 1, minWidth: 160 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          type="text"
          value={value}
          onChange={(e) => {
            onTypeFreeText(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          style={{ ...cellInputStyle, flex: 1 }}
          placeholder="Search service, medicine, product, or type custom item..."
        />
        {inventoryItemId && <SourceChip kind="inventory" onClear={onClearInventory} />}
        {!inventoryItemId && serviceId && <SourceChip kind="service" onClear={onClearService} />}
      </div>
      {open && totalResults > 0 && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            maxHeight: 320,
            overflowY: 'auto',
            zIndex: 50,
            minWidth: 280,
          }}
        >
          {medicineInventoryRows.length > 0 && <SectionLabel>Inventory Medicine</SectionLabel>}
          {medicineInventoryRows.slice(0, 8).map((row) => {
            const selection = pickerCandidateRowToSelection(row);
            const meta = [
              selection.sku && `SKU ${selection.sku}`,
              selection.inventoryUnit && `Unit ${selection.inventoryUnit}`,
              selection.currentStockDisplay != null && `Stock ${selection.currentStockDisplay}`,
              selection.sellingPrice != null && `${fmt(selection.sellingPrice)}`,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <ResultRow
                key={`inv-${row.id}`}
                badge="Inventory"
                badgeKind="inventory"
                title={selection.itemName || '(unnamed)'}
                meta={meta}
                onClick={() => {
                  onSelectInventory(selection);
                  setOpen(false);
                }}
              />
            );
          })}
          {/* PRD.205 — Inventory Product results. Same write path as Medicine
              (inventory_item_id + invoice snapshots, no stock movement). */}
          {productInventoryRows.length > 0 && <SectionLabel>Inventory Product</SectionLabel>}
          {productInventoryRows.slice(0, 8).map((row) => {
            const selection = pickerCandidateRowToSelection(row);
            const meta = [
              selection.sku && `SKU ${selection.sku}`,
              selection.brandName && `Brand ${selection.brandName}`,
              selection.inventoryUnit && `Unit ${selection.inventoryUnit}`,
              selection.currentStockDisplay != null && `Stock ${selection.currentStockDisplay}`,
              selection.sellingPrice != null && `${fmt(selection.sellingPrice)}`,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <ResultRow
                key={`prod-${row.id}`}
                badge="Product"
                badgeKind="product"
                title={selection.itemName || '(unnamed)'}
                meta={meta}
                onClick={() => {
                  onSelectInventory(selection);
                  setOpen(false);
                }}
              />
            );
          })}
          {/* PRD.261 — Inventory Package results. Sold as one line at the
              package price; no stock shown (packages aren't physical stock)
              and child items are never expanded onto the invoice. */}
          {packageInventoryRows.length > 0 && <SectionLabel>Inventory Package</SectionLabel>}
          {packageInventoryRows.slice(0, 8).map((row) => {
            const selection = pickerCandidateRowToSelection(row);
            const meta = [
              selection.sku && `Code ${selection.sku}`,
              selection.sellingPrice != null && `${fmt(selection.sellingPrice)}`,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <ResultRow
                key={`pkg-${row.id}`}
                badge="Package"
                badgeKind="package"
                title={selection.itemName || '(unnamed)'}
                meta={meta}
                onClick={() => {
                  onSelectInventory(selection);
                  setOpen(false);
                }}
              />
            );
          })}
          {/* PRD.264 — Sellable Supply results. Added like a product line;
              fulfillment (release/dispense) follows existing rules. */}
          {supplyInventoryRows.length > 0 && <SectionLabel>Inventory Supply</SectionLabel>}
          {supplyInventoryRows.slice(0, 8).map((row) => {
            const selection = pickerCandidateRowToSelection(row);
            const kind =
              row.supply_usage_type === 'expendable'
                ? 'Expendable'
                : row.supply_usage_type === 'consumable'
                  ? 'Consumable'
                  : null;
            const meta = [
              kind,
              selection.sku && `Code ${selection.sku}`,
              selection.inventoryUnit && `Unit ${selection.inventoryUnit}`,
              selection.sellingPrice != null && `${fmt(selection.sellingPrice)}`,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <ResultRow
                key={`sup-${row.id}`}
                badge="Supply"
                badgeKind="supply"
                title={selection.itemName || '(unnamed)'}
                meta={meta}
                onClick={() => {
                  onSelectInventory(selection);
                  setOpen(false);
                }}
              />
            );
          })}
          {/* PRD.266 — Vaccination (stock-tracked) billable lines. */}
          {vaccinationInventoryRows.length > 0 && (
            <SectionLabel>Inventory Vaccination</SectionLabel>
          )}
          {vaccinationInventoryRows.slice(0, 8).map((row) => {
            const selection = pickerCandidateRowToSelection(row);
            const meta = [
              selection.sku && `Code ${selection.sku}`,
              selection.inventoryUnit && `Unit ${selection.inventoryUnit}`,
              selection.sellingPrice != null && `${fmt(selection.sellingPrice)}`,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <ResultRow
                key={`vac-${row.id}`}
                badge="Vaccination"
                badgeKind="vaccination"
                title={selection.itemName || '(unnamed)'}
                meta={meta}
                onClick={() => {
                  onSelectInventory(selection);
                  setOpen(false);
                }}
              />
            );
          })}
          {/* PRD.266 — Injection (non-stock) billable lines. */}
          {injectionInventoryRows.length > 0 && <SectionLabel>Inventory Injection</SectionLabel>}
          {injectionInventoryRows.slice(0, 8).map((row) => {
            const selection = pickerCandidateRowToSelection(row);
            const meta = [
              selection.sku && `Code ${selection.sku}`,
              selection.inventoryUnit && `Unit ${selection.inventoryUnit}`,
              selection.sellingPrice != null && `${fmt(selection.sellingPrice)}`,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <ResultRow
                key={`inj-${row.id}`}
                badge="Injection"
                badgeKind="injection"
                title={selection.itemName || '(unnamed)'}
                meta={meta}
                onClick={() => {
                  onSelectInventory(selection);
                  setOpen(false);
                }}
              />
            );
          })}
          {serviceMatches.length > 0 && <SectionLabel>Services</SectionLabel>}
          {serviceMatches.map((svc) => {
            const meta =
              svc.price_cents != null && svc.price_cents > 0 ? fmt(svc.price_cents / 100) : '';
            return (
              <ResultRow
                key={`svc-${svc.id}`}
                badge="Service"
                badgeKind="service"
                title={svc.name ?? '(unnamed service)'}
                meta={meta}
                onClick={() => {
                  onSelectService(svc.name ?? '', svc.price_cents, svc.id);
                  setOpen(false);
                }}
              />
            );
          })}
          {showManualOption && (
            <>
              <SectionLabel>Manual</SectionLabel>
              <ResultRow
                badge="Manual"
                badgeKind="manual"
                title={`Use "${value.trim()}" as custom line item`}
                meta=""
                onClick={() => {
                  // Already typed — just close the dropdown. The parent
                  // already updated state via onTypeFreeText.
                  setOpen(false);
                }}
              />
            </>
          )}
        </div>
      )}
      {open && totalResults === 0 && value.trim().length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            padding: '8px 10px',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            zIndex: 50,
          }}
        >
          No service or medicine match. Press elsewhere to keep typed text as a manual line.
        </div>
      )}
    </div>
  );
}

// PRD.264 — Billing "Add from Used Supplies". Lists ready-for-billing used
// supplies for the invoice's patient/encounter; adding creates invoice lines
// (stock already deducted at usage — never deducted again).
function AddFromUsedSuppliesButton({
  patientId,
  encounterId,
  onAdd,
  primary,
}: {
  patientId: string;
  encounterId: string;
  onAdd: (rows: UsedSupplyRow[]) => void;
  primary: string;
}) {
  const [open, setOpen] = useState(false);
  const { data: rows = [] } = usePowerSyncQuery<UsedSupplyRow>(
    qReadyForBillingUsedSupplies(patientId, encounterId || null),
  );
  if (!patientId) return null;

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          border: 'none',
          background: 'none',
          color: primary,
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          cursor: 'pointer',
          padding: 'var(--sp-1) 0',
        }}
      >
        + Add from Used Supplies{rows.length > 0 ? ` (${rows.length})` : ''}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 26,
            left: 0,
            zIndex: 40,
            minWidth: 300,
            maxHeight: 260,
            overflowY: 'auto',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: 'var(--sp-2)',
          }}
        >
          {rows.length === 0 ? (
            <div
              style={{
                padding: 'var(--sp-2)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
              }}
            >
              No ready-for-billing used supplies for this visit.
            </div>
          ) : (
            <>
              {rows.map((r) => (
                <div
                  key={r.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 'var(--sp-2)',
                    padding: 'var(--sp-1) var(--sp-2)',
                  }}
                >
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text)' }}>
                    {r.item_name_snapshot ?? 'Supply'} · Qty{' '}
                    {Number.isInteger(r.quantity ?? 0)
                      ? r.quantity
                      : Number((r.quantity ?? 0).toFixed(3))}
                    {r.batch_no_snapshot ? ` · ${r.batch_no_snapshot}` : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() => onAdd([r])}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: primary,
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Add
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  onAdd(rows);
                  setOpen(false);
                }}
                style={{
                  marginTop: 'var(--sp-1)',
                  width: '100%',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-surface)',
                  color: primary,
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '4px 0',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                Add all
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '6px 10px 2px',
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'var(--text-muted, #64748b)',
        background: 'var(--bg-surface-2, #fafafa)',
      }}
    >
      {children}
    </div>
  );
}

function ResultRow({
  badge,
  badgeKind,
  title,
  meta,
  onClick,
}: {
  badge: string;
  // PRD.205 — `product`; PRD.261 — `package` (violet); PRD.264 — `supply` (teal).
  // PRD.266 — `vaccination` (amber) and `injection` (rose).
  badgeKind:
    | 'inventory'
    | 'product'
    | 'package'
    | 'supply'
    | 'service'
    | 'vaccination'
    | 'injection'
    | 'manual';
  title: string;
  meta: string;
  onClick: () => void;
}) {
  const palette: Record<typeof badgeKind, { bg: string; color: string }> = {
    inventory: { bg: '#eff6ff', color: '#1d4ed8' },
    product: { bg: '#eef2ff', color: '#4338ca' },
    package: { bg: '#f3e8ff', color: '#7c3aed' },
    supply: { bg: '#ecfeff', color: '#0e7490' },
    service: { bg: '#f0fdf4', color: '#15803d' },
    vaccination: { bg: '#fffbeb', color: '#b45309' },
    injection: { bg: '#fff1f2', color: '#be123c' },
    manual: { bg: 'var(--bg-surface-2, #f3f4f6)', color: 'var(--text-muted, #64748b)' },
  };
  const p = palette[badgeKind];
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        textAlign: 'left',
        padding: '8px 10px',
        border: 'none',
        borderBottom: '1px solid var(--border)',
        background: 'transparent',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          'var(--bg-hover, rgba(0,0,0,0.04))';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      <span
        style={{
          fontSize: 9,
          fontWeight: 700,
          padding: '2px 6px',
          borderRadius: 'var(--radius-full, 9999px)',
          background: p.bg,
          color: p.color,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          flexShrink: 0,
        }}
      >
        {badge}
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </div>
        {meta && (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              marginTop: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {meta}
          </div>
        )}
      </span>
    </button>
  );
}

function SourceChip({ kind, onClear }: { kind: 'inventory' | 'service'; onClear: () => void }) {
  const palette = {
    inventory: {
      label: 'INV',
      bg: 'var(--color-success-bg, #dcfce7)',
      color: 'var(--color-success-text, #166534)',
      title: 'Linked to inventory item — click × to switch to free text',
    },
    service: {
      label: 'SVC',
      bg: '#eff6ff',
      color: '#1d4ed8',
      title: 'Linked to service — click × to switch to free text',
    },
  }[kind];
  return (
    <span
      title={palette.title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: '0 6px',
        height: 22,
        borderRadius: 'var(--radius-full, 999px)',
        background: palette.bg,
        color: palette.color,
        fontSize: 10,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {palette.label}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
        aria-label={`Unlink ${kind}`}
        style={{
          marginLeft: 2,
          width: 14,
          height: 14,
          padding: 0,
          borderRadius: '50%',
          border: 'none',
          background: 'transparent',
          color: 'inherit',
          cursor: 'pointer',
          fontSize: 12,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </span>
  );
}
